import { adminAuthService } from './adminAuthService';
import axiosInstance from './axiosConfig'; // Use axiosInstance with interceptors
import { featureFlags } from '../config/featureFlags';

// Admin API response wrapper
interface ApiResponse<T> {
  success: boolean;
  error?: string;
  data?: T | null;
}

// Active Trading Bots Response interface
export interface ActiveTradingBotsResponse {
  count: number;
  bots: Array<{
    id: number;
    name: string;
    status: string;
    strategyType: string;
    paperTrading: boolean;
  }>;
}

// Dashboard statistics interface - Updated to include KYC/KYB summaries
export interface DashboardStats {
  totalCustomers: number;
  activeCustomers: number;
  totalAccounts: number;
  activeAccounts: number;
  frozenAccounts: number;
  totalBalance: number;
  pendingTransactions: number;
  completedTransactions: number;
  todayTransactions: number;
  pendingAlerts: number;
  openAlerts: number;
  resolvedAlerts: number;
  systemHealth: 'HEALTHY' | 'WARNING' | 'CRITICAL';
  lastUpdate: string;
  kycSummary?: {
    total: number;
    pending: number;
    approved: number;
    rejected: number;
    inReview: number;
    today: number;
  };
  kybSummary?: {
    total: number;
    pending: number;
    approved: number;
    rejected: number;
    inReview: number;
    today: number;
  };
}

// Customer interface
interface AdminCustomer {
  id: number;
  username: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  status: string;
  kycStatus?: string;
  riskRating?: string;
  activationDate?: string;
  createdAt?: string;
  lastLoginAt?: string;
  lastLoginIp?: string;
  isActive?: boolean;
  isPep?: boolean;
  isSanctioned?: boolean;
  address?: string;
  country?: string;
  dateOfBirth?: string;
  avatarBase64?: string;
  accounts?: any[];
  alerts?: any[];
  notes?: string;
  portfolioSummary?: {
    cryptoTotalValue?: number;
    cryptoCoinsCount?: number;
    cryptoDailyChange?: number;
    cryptoDailyChangePercentage?: number;
    assetsTotalValue?: number;
    assetsHoldingsCount?: number;
    assetsDailyChange?: number;
    assetsDailyChangePercentage?: number;
    totalPortfolioValue?: number;
    totalDailyChange?: number;
    totalDailyChangePercentage?: number;
  };
  addressInfo?: {
    street?: string;
    city?: string;
    state?: string;
    postalCode?: string;
    country?: string;
  };
  employmentInfo?: {
    employerName?: string;
    jobTitle?: string;
    monthlyIncome?: number;
    duration?: string;
  };
  // --- SEPA & Risk fields ---
  userType?: string; // RETAIL, BUSINESS_NORMAL, BUSINESS_HIGH_RISK
  riskCategory?: string; // NORMAL, HIGH_RISK
  reviewRequired?: boolean;
  sepaDailyLimit?: number;
  sepaMonthlyLimit?: number;
  sepaYearlyLimit?: number;
  sepaSingleTransactionLimit?: number;
  sepaLimitCurrency?: string;
  
  // Parent-Child relationship fields for frontend grouping
  parentCustomerId?: number; // ID of parent customer (null if this is a parent)
  parentCustomerName?: string; // Name of parent customer
  childCustomerIds?: number[]; // List of child customer IDs
  childCustomerNames?: string[]; // List of child customer names
  accountType?: string; // "PARENT" or "CHILD"
  isParentAccount?: boolean; // true if this is a parent account
  hasChildAccounts?: boolean; // true if this parent has child accounts
}

// User interface for multi-signature wallet signatories
interface User {
  id: number;
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string; // "USER" or "ADMIN"
}

// Account interface
interface AdminAccount {
  id: number;
  accountNumber: string;
  customerId: number;
  customerName: string;
  type: 'SAVINGS' | 'CHECKING' | 'BUSINESS';
  balance: number;
  status: 'ACTIVE' | 'FROZEN' | 'CLOSED';
  lastTransaction?: string;
  createdDate: string;
}

// Transaction interface
interface AdminTransaction {
  id: number;
  transactionId: string | null;
  customerId: number;
  customerName: string;
  customerEmail: string;
  accountId: number;
  accountNumber: string;
  type: 'DEPOSIT' | 'WITHDRAWAL' | 'TRANSFER' | 'PAYMENT' | 'CREDIT' | 'DEBIT' | 'CRYPTO_BUY' | 'CRYPTO_SELL' | 'CRYPTO_TRADE' | 'STOCK_BUY' | 'STOCK_SELL' | 'OPTION_BUY' | 'OPTION_SELL' | 'MT5_DEPOSIT' | 'MT5_WITHDRAWAL' | 'MT5_TRADE';
  amount: number;
  currency: string | null;
  transactionDate: string;
  description: string;
  category: string;
  status: 'PENDING' | 'COMPLETED' | 'REJECTED' | 'PROCESSING' | 'FAILED' | 'CANCELLED';
  paymentMethod: string | null;
  recipientAccount: string | null;
  recipientName: string | null;
  reference: string | null;
  isSuspicious: boolean | null;
  requiresApproval: boolean | null;
  approvedBy: string | null;
  approvedAt: string | null;
  rejectionReason: string | null;
  ipAddress: string | null;
  deviceFingerprint: string | null;
  balanceBefore: number | null;
  balanceAfter: number | null;
  riskScore: number | null;
  isReversible: boolean | null;
  reversedAt: string | null;
  reversedBy: string | null;
  // Legacy fields for backward compatibility
  fromAccountId?: number;
  toAccountId?: number;
  processedDate?: string;
}

// Compliance alert interface
export interface ComplianceAlert {
  id: number;
  alertId: string | number;
  alertType: string;
  type?: string;
  severity: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  description: string;
  customerId: number;
  customerName?: string;
  status: 'OPEN' | 'IN_PROGRESS' | 'RESOLVED' | 'DISMISSED';
  assignedTo?: string;
  assignee?: string;
  assigneeName?: string;
  createdAt: string;
  updatedDate?: string;
  title?: string;
  requiresAction?: boolean;
}

// Transaction filter interface
interface TransactionFilter {
  page: number;
  size: number;
  type?: string;
  status?: string;
  fromDate?: string;
  toDate?: string;
  customerId?: number;
  sortBy?: string;
  sortDirection?: 'ASC' | 'DESC';
}

// KYC/KYB Submission interfaces
export interface KycSubmission {
  id: number;
  userId: number;
  userName: string;
  email: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  mobileNumber?: string;
  submittedAt: string;
  utilityBillPath?: string;
  passportPath?: string;
  idFrontPath?: string;
  idBackPath?: string;
  status: string;
  comments?: string;
  riskLevel?: string;
  productId: number;
  productType: string;
  productName: string;
}

export interface KybSubmission {
  id: number;
  userId: number;
  userName: string;
  email: string;
  firstName?: string;
  lastName?: string;
  contactFirstName?: string;
  contactLastName?: string;
  submittedAt: string;
  status: string;
  riskLevel?: string;
  companyName: string;
  productId: number;
  productType: string;
  productName: string;
  registrationNumber: string;
  dateOfIncorporation: string;
  countryOfIncorporation: string;
  businessType: string;
  businessTypeOther?: string;
  industry: string;
  website: string;
  phone: string;
  mobileNumber?: string;
  contactPhone?: string;
  registeredAddress: {
    street: string;
    city: string;
    state: string;
    postalCode: string;
    country: string;
  };
  businessAddress: {
    street: string;
    city: string;
    state: string;
    postalCode: string;
    country: string;
  };
  directors: Array<{
    name: string;
    position: string;
    nationality: string;
    shareholding: string;
    address: string;
  }>;
  signatories: Array<{
    name: string;
    position: string;
    authority: string;
    address: string;
  }>;
  businessActivity: string;
  expectedMonthlyTurnover: string;
  sourceOfFunds: string;
  purposeOfAccount: string;
  underSanctions: boolean;
  sanctionType?: string;
  sanctionFrom?: string;
  pep: boolean;
  pepDetails?: Array<{
    name: string;
    position: string;
    organization: string;
    country: string;
    untilWhen: string;
  }>;
  documents: {
    certificateOfIncorporation: boolean;
    memorandumOfAssociation: boolean;
    articlesOfAssociation: boolean;
    boardResolution: boolean;
    beneficialOwnership: boolean;
    financialStatements: boolean;
    businessPlan: boolean;
    other: boolean;
    otherSpecify?: string;
  };
  documentComments: {
    certificateOfIncorporation?: string;
    memorandumOfAssociation?: string;
    articlesOfAssociation?: string;
    boardResolution?: string;
    beneficialOwnership?: string;
    financialStatements?: string;
    businessPlan?: string;
    other?: string;
  };
  sumsubVerificationCompleted: boolean;
  terms: boolean;
  marketing: boolean;
  signatureDataUrl?: string;
  certificateOfIncorporationPath?: string;
  memorandumPath?: string;
  articlesPath?: string;
  boardResolutionPath?: string;
  beneficialOwnershipPath?: string;
  financialStatementsPath?: string;
  businessPlanPath?: string;
  otherDocumentsPath?: string;
  businessUtilityBillPath?: string;
  comments?: string;
  reviewedBy?: string;
  reviewedAt?: string;
}

// Asset Token Investment tracking interfaces
export interface AssetTokenInvestment {
  id: number;
  assetTokenId: number;
  userId: number;
  userName: string;
  investmentAmount: number;
  investmentDate: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface TransactionCounts {
  bankingCount: number;
  cryptoCount: number;
  tradingCount: number;
  mt5Count: number;
}

// KYC/KYB Summary interface
export interface KycKybSummary {
  total: number;
  pending: number;
  approved: number;
  rejected: number;
  inReview: number;
  today: number;
}

// Hedge positions (per-leg) for a trading strategy
export interface HedgePosition {
  id: number;
  strategyId: number;
  symbol: string;
  side: 'LONG' | 'SHORT';
  positionId: string | null;
  status: 'OPEN' | 'CLOSED' | 'CANCELLED' | 'ERROR';
  size: number | null;
  entryPrice: number | null;
  exitPrice: number | null;
  leverage: number | null;
  pnl: number | null;
  fees: number | null;
  createdAt: string;
  updatedAt: string;
  closedAt: string | null;
}

export interface AiPerformanceSnapshotTradeRow {
  symbol?: string;
  side?: string;
  label?: string;
  closeTimeMs?: number;
  closedDisplay?: string;
  pnlGross?: number | string;
  fees?: number | string;
  netPnl?: number | string;
}

export interface AiPerformanceSnapshotCoinStats {
  symbol?: string;
  tradeCount?: number;
  grossPnl?: number | string;
  totalFees?: number | string;
  netPnl?: number | string;
  winCount?: number;
  lossCount?: number;
  winRate?: number | string;
}

export interface AiPerformanceSnapshotCoinSideStats {
  symbol?: string;
  side?: string;
  label?: string;
  tradeCount?: number;
  grossPnl?: number | string;
  netPnl?: number | string;
  winCount?: number;
  lossCount?: number;
}

export interface AiPerformanceSnapshotEvaluation {
  title?: string;
  requestedWindow?: number;
  evaluateAll?: boolean;
  actualCount?: number;
  totalTradesAvailable?: number;
  grossPnl?: number | string;
  totalFees?: number | string;
  netPnl?: number | string;
  winCount?: number;
  lossCount?: number;
  breakEvenCount?: number;
  winRate?: number | string;
  returnOnOrderSize?: number | string | null;
  orderSize?: number | string | null;
  insufficientData?: boolean;
  minTradesRequired?: number;
  dataSource?: string;
  trades?: AiPerformanceSnapshotTradeRow[];
  byCoin?: AiPerformanceSnapshotCoinStats[];
  byCoinSide?: AiPerformanceSnapshotCoinSideStats[];
}

export interface AiPerformanceSnapshotPayload {
  strategyId?: number;
  strategyName?: string;
  snapshotEnabled?: boolean;
  evaluation?: AiPerformanceSnapshotEvaluation;
}

class AdminApiService {
  private baseUrl = featureFlags.general.apiUrl;

  private async makeRequest<T>(endpoint: string, options: RequestInit = {}, token?: string): Promise<ApiResponse<T>> {
    try {
      // Note: We don't manually set Authorization header here because
      // axiosInstance interceptor automatically handles it from Redux store
      // This ensures we always use the latest token, especially after refresh
      
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        ...(options.headers as Record<string, string> || {}),
      };

      // Only add authorization header if explicitly provided (for special cases)
      // Otherwise, let axiosInstance interceptor handle it
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      console.log('makeRequest: Making API call to:', endpoint);
      console.log('makeRequest: Request headers (Authorization handled by interceptor):', headers);
      console.log('makeRequest: Base URL:', this.baseUrl);

      // Use axiosInstance which has the refresh interceptor
      // The interceptor will automatically add the Authorization header from Redux store
      let response: any;
      
      // Pass headers to axiosInstance (interceptor will add/update Authorization header)
      const requestConfig = { headers };
      
      if (options.method === 'POST') {
        const body = options.body instanceof FormData ? options.body : 
                    (options.body ? (headers['Content-Type'] === 'text/plain' ? options.body : JSON.parse(options.body as string)) : undefined);
        // If it's FormData, remove Content-Type header to let browser set it
        if (options.body instanceof FormData) {
          delete headers['Content-Type'];
        }
        response = await axiosInstance.post(endpoint, body, { ...requestConfig, headers });
      } else if (options.method === 'PUT') {
        const body = options.body instanceof FormData ? options.body :
                    (options.body ? (headers['Content-Type'] === 'text/plain' ? options.body : JSON.parse(options.body as string)) : undefined);
        if (options.body instanceof FormData) {
          delete headers['Content-Type'];
        }
        response = await axiosInstance.put(endpoint, body, { ...requestConfig, headers });
      } else if (options.method === 'PATCH') {
        const body = options.body instanceof FormData ? options.body :
                    (options.body ? (headers['Content-Type'] === 'text/plain' ? options.body : JSON.parse(options.body as string)) : undefined);
        if (options.body instanceof FormData) {
          delete headers['Content-Type'];
        }
        response = await axiosInstance.patch(endpoint, body, { ...requestConfig, headers });
      } else if (options.method === 'DELETE') {
        response = await axiosInstance.delete(endpoint, requestConfig);
      } else {
        console.log('makeRequest: Making GET request to:', endpoint);
        response = await axiosInstance.get(endpoint, requestConfig);
        console.log('makeRequest: GET response received:', response.status, response.data);
      }

      // Axios response structure is different from fetch
      const responseData = response.data;
      console.log('makeRequest: Response data:', responseData);
      
      // Check for error responses (both success: false and status: 'Failed')
      if (responseData.success === false || responseData.status === 'Failed' || responseData.status === 'error') {
        // If the backend is not available, return mock data for development
        const mockData = this.getMockData(endpoint);
        if (mockData) {
          return {
            data: mockData,
            success: true
          };
        }
        throw new Error(responseData.message || 'API request failed');
      }

      // Handle both wrapped responses (with data property) and direct responses
      // Backend returns StandardApiResponse: { status: 'success', code: 200, data: [...], ... }
      const actualData = responseData.data !== undefined ? responseData.data : responseData;
      
      // Extract pagination metadata if present
      const paginationMetadata = {
        totalElements: responseData.totalElements,
        totalPages: responseData.totalPages,
        currentPage: responseData.currentPage,
        pageSize: responseData.pageSize
      };
      
      return {
        data: actualData,
        success: true,
        ...paginationMetadata
      };
    } catch (error) {
      console.error('makeRequest: API request failed for endpoint:', endpoint, error);
      console.error('makeRequest: Error details:', {
        message: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : undefined,
        name: error instanceof Error ? error.name : undefined
      });
      
      // Try to return mock data if API fails
      const mockData = this.getMockData(endpoint);
      if (mockData) {
        console.log('makeRequest: Returning mock data for endpoint:', endpoint);
        return {
          data: mockData,
          success: true
        };
      }
      
      return {
        data: null as any,
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  }

  // Fetch a file (blob) using the authenticated axios instance (includes auth headers/interceptors)
  async getFileBlobByUrl(url: string): Promise<Blob> {
    const response = await axiosInstance.get(url, { responseType: 'blob', withCredentials: true });
    return response.data as Blob;
  }

  async getFileBlob(fileIdentifier: string): Promise<Blob> {
    const url = `/admin/files-simple/${fileIdentifier}`;
    const response = await axiosInstance.get(url, { responseType: 'blob', withCredentials: true });
    return response.data as Blob;
  }

  // Generic multipart POST helper using authenticated axios instance
  async postMultipart<T = any>(endpoint: string, form: FormData): Promise<ApiResponse<T>> {
    try {
      const res = await axiosInstance.post(endpoint, form, {
        withCredentials: true,
        headers: {
          // Ensure axios does NOT JSON-encode and lets the browser set the boundary
          'Content-Type': 'multipart/form-data'
        },
        transformRequest: [(data) => data]
      });
      const body = res.data as any;
      const isOk = (body && (body.success === true)) || (body?.status === 'Successful' && body?.code === 200);
      if (isOk) {
        return { success: true, data: body.data as T } as ApiResponse<T>;
      }
      return {
        success: false,
        error: body?.message || (Array.isArray(body?.errors) ? body.errors.join(', ') : 'Upload failed')
      } as ApiResponse<T>;
    } catch (error: any) {
      console.error('postMultipart error:', error);
      return { success: false, error: error?.response?.data?.message || error.message || 'Upload failed' } as any;
    }
  }

  async deleteAttachment(attachmentId: number): Promise<ApiResponse<void>> {
    try {
      const res = await axiosInstance.delete(`/admin/documents/${attachmentId}`, { withCredentials: true });
      const body = res.data as any;
      const ok = (body && body.success === true) || (body?.status === 'Successful' && body?.code === 200);
      return ok ? { success: true } as ApiResponse<void> : { success: false, error: body?.message || 'Delete failed' } as any;
    } catch (e: any) {
      return { success: false, error: e?.response?.data?.message || e.message || 'Delete failed' } as any;
    }
  }

  async delete(endpoint: string): Promise<ApiResponse<any>> {
    try {
      const res = await axiosInstance.delete(endpoint, { withCredentials: true });
      const body = res.data as any;
      const ok = (body && body.success === true) || (body?.status === 'Successful' && body?.code === 200);
      return ok ? { success: true, data: body.data } as ApiResponse<any> : { success: false, error: body?.message || 'Delete failed' } as any;
    } catch (e: any) {
      return { success: false, error: e?.response?.data?.message || e.message || 'Delete failed' } as any;
    }
  }

  private getMockData(endpoint: string): any {
    // Remove the mock data for admin users since we're now using real data
    if (endpoint.includes('/admin/users')) {
      return null; // Return null to prevent fallback to mock data
    }
    
    // Dashboard stats
    if (endpoint.includes('/admin/dashboard/stats')) {
      return {
        totalCustomers: 15420,
        activeCustomers: 12850,
        totalAccounts: 18750,
        activeAccounts: 17200,
        frozenAccounts: 1550,
        totalBalance: 2850000.75,
        pendingTransactions: 24,
        completedTransactions: 8547,
        todayTransactions: 156,
        pendingAlerts: 3,
        openAlerts: 8,
        resolvedAlerts: 245,
        systemHealth: 'HEALTHY' as const,
        lastUpdate: new Date().toISOString()
      };
    }
    
    // Portfolio stats
    if (endpoint.includes('/admin/dashboard/portfolio-stats')) {
      return {
        totalCryptoValue: 125000000,
        totalAssetValue: 450000000,
        totalPortfolioValue: 575000000,
        cryptoGrowth: 12.5,
        assetGrowth: 8.3
      };
    }
    
    // Compliance alerts
    if (endpoint.includes('/admin/compliance/alerts')) {
      return {
        content: [
          {
            id: 1,
            alertId: 'ALT-2024-001',
            alertType: 'LARGE_DEPOSIT',
            type: 'LARGE_DEPOSIT',
            severity: 'HIGH',
            description: 'Large cash deposit of $15,000 detected',
            customerId: 3,
            customerName: 'Bob Johnson',
            status: 'OPEN',
            assignedTo: 'admin',
            assignee: 'System Admin',
            createdAt: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
            updatedDate: new Date().toISOString(),
            title: 'Large Cash Deposit Alert',
            requiresAction: true
          },
          {
            id: 2,
            alertId: 'ALT-2024-002',
            alertType: 'SUSPICIOUS_TRANSACTION',
            type: 'SUSPICIOUS_TRANSACTION',
            severity: 'MEDIUM',
            description: 'Multiple transactions to high-risk countries',
            customerId: 1,
            customerName: 'John Doe',
            status: 'IN_PROGRESS',
            assignedTo: 'admin',
            assignee: 'System Admin',
            createdAt: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(),
            updatedDate: new Date(Date.now() - 1 * 60 * 60 * 1000).toISOString(),
            title: 'Suspicious Transaction Alert',
            requiresAction: true
          }
          // MULTIPLE_FAILED_LOGINS is no longer mocked — real alerts are created on account lockout.
        ],
        totalElements: 2,
        totalPages: 1
      };
    }


    if (endpoint.includes('/admin/customers')) {
      return {
        content: [
          {
            id: 1,
            firstName: 'John',
            lastName: 'Doe',
            email: 'john.doe@example.com',
            phoneNumber: '+1234567890',
            status: 'ACTIVE',
            accountsCount: 2,
            totalBalance: 15000.50,
            createdDate: new Date().toISOString(),
            lastLogin: new Date().toISOString(),
            portfolioSummary: {
              cryptoTotalValue: 2500.75,
              cryptoCoinsCount: 3,
              cryptoDailyChange: 125.30,
              cryptoDailyChangePercentage: 5.26,
              assetsTotalValue: 8750.25,
              assetsHoldingsCount: 5,
              assetsDailyChange: -45.80,
              assetsDailyChangePercentage: -0.52,
              totalPortfolioValue: 11251.00,
              totalDailyChange: 79.50,
              totalDailyChangePercentage: 0.71
            }
          },
          {
            id: 2,
            firstName: 'Jane',
            lastName: 'Smith',
            email: 'jane.smith@example.com',
            phoneNumber: '+1234567891',
            status: 'ACTIVE',
            accountsCount: 1,
            totalBalance: 8500.25,
            createdDate: new Date().toISOString(),
            lastLogin: new Date().toISOString(),
            portfolioSummary: {
              cryptoTotalValue: 1200.00,
              cryptoCoinsCount: 2,
              cryptoDailyChange: -25.50,
              cryptoDailyChangePercentage: -2.08,
              assetsTotalValue: 3500.75,
              assetsHoldingsCount: 4,
              assetsDailyChange: 78.25,
              assetsDailyChangePercentage: 2.28,
              totalPortfolioValue: 4700.75,
              totalDailyChange: 52.75,
              totalDailyChangePercentage: 1.13
            }
          },
          {
            id: 3,
            firstName: 'Bob',
            lastName: 'Johnson',
            email: 'bob.johnson@example.com',
            phoneNumber: '+1234567892',
            status: 'SUSPENDED',
            accountsCount: 3,
            totalBalance: 25000.75,
            createdDate: new Date().toISOString(),
            lastLogin: new Date().toISOString(),
            portfolioSummary: {
              cryptoTotalValue: 0.00,
              cryptoCoinsCount: 0,
              cryptoDailyChange: 0.00,
              cryptoDailyChangePercentage: 0.00,
              assetsTotalValue: 15750.50,
              assetsHoldingsCount: 8,
              assetsDailyChange: 234.80,
              assetsDailyChangePercentage: 1.52,
              totalPortfolioValue: 15750.50,
              totalDailyChange: 234.80,
              totalDailyChangePercentage: 1.52
            }
          }
        ],
        totalElements: 3,
        totalPages: 1
      };
    }

    if (endpoint.includes('/admin/accounts')) {
      return {
        content: [
          {
            id: 1,
            accountNumber: 'ACC001234567',
            customerId: 1,
            customerName: 'John Doe',
            type: 'CHECKING',
            balance: 5000.50,
            status: 'ACTIVE',
            lastTransaction: new Date().toISOString(),
            createdDate: new Date().toISOString()
          },
          {
            id: 2,
            accountNumber: 'ACC001234568',
            customerId: 1,
            customerName: 'John Doe',
            type: 'SAVINGS',
            balance: 10000.00,
            status: 'ACTIVE',
            lastTransaction: new Date().toISOString(),
            createdDate: new Date().toISOString()
          },
          {
            id: 3,
            accountNumber: 'ACC001234569',
            customerId: 2,
            customerName: 'Jane Smith',
            type: 'CHECKING',
            balance: 8500.25,
            status: 'ACTIVE',
            lastTransaction: new Date().toISOString(),
            createdDate: new Date().toISOString()
          }
        ],
        totalElements: 3,
        totalPages: 1
      };
    }

    if (endpoint.includes('/admin/transactions')) {
      return {
        content: [
          {
            id: 1,
            transactionId: 'TXN0000001',
            customerId: 1,
            customerName: 'John Doe',
            customerEmail: 'john.doe@example.com',
            accountId: 1,
            accountNumber: 'ACC001234567',
            type: 'DEPOSIT',
            amount: 1500.00,
            currency: 'USD',
            transactionDate: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(), // 2 hours ago
            description: 'Cash deposit at ATM',
            category: 'DEPOSIT',
            status: 'COMPLETED',
            paymentMethod: 'ATM',
            recipientAccount: null,
            recipientName: null,
            reference: null,
            isSuspicious: false,
            requiresApproval: false,
            approvedBy: 'admin1',
            approvedAt: new Date(Date.now() - 1 * 60 * 60 * 1000).toISOString(),
            rejectionReason: null,
            ipAddress: '192.168.1.100',
            deviceFingerprint: 'abc123',
            balanceBefore: 5000.50,
            balanceAfter: 6500.50,
            riskScore: 0,
            isReversible: true,
            reversedAt: null,
            reversedBy: null
          },
          {
            id: 2,
            transactionId: 'TXN0000002',
            customerId: 1,
            customerName: 'John Doe',
            customerEmail: 'john.doe@example.com',
            accountId: 2,
            accountNumber: 'ACC001234568',
            type: 'TRANSFER',
            amount: 500.00,
            currency: 'USD',
            transactionDate: new Date(Date.now() - 30 * 60 * 1000).toISOString(), // 30 minutes ago
            description: 'Transfer to Jane Smith',
            category: 'TRANSFER',
            status: 'PENDING',
            paymentMethod: 'INTERNET_BANKING',
            recipientAccount: 'ACC001234569',
            recipientName: 'Jane Smith',
            reference: 'INV001',
            isSuspicious: false,
            requiresApproval: true,
            approvedBy: null,
            approvedAt: null,
            rejectionReason: null,
            ipAddress: '192.168.1.101',
            deviceFingerprint: 'def456',
            balanceBefore: 10000.00,
            balanceAfter: 9500.00,
            riskScore: 0,
            isReversible: true,
            reversedAt: null,
            reversedBy: null
          },
          {
            id: 3,
            transactionId: 'TXN0000003',
            customerId: 2,
            customerName: 'Jane Smith',
            customerEmail: 'jane.smith@example.com',
            accountId: 1,
            accountNumber: 'ACC001234567',
            type: 'WITHDRAWAL',
            amount: 200.00,
            currency: 'USD',
            transactionDate: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(), // 1 day ago
            description: 'ATM withdrawal',
            category: 'WITHDRAWAL',
            status: 'COMPLETED',
            paymentMethod: 'ATM',
            recipientAccount: null,
            recipientName: null,
            reference: null,
            isSuspicious: false,
            requiresApproval: false,
            approvedBy: 'admin2',
            approvedAt: new Date(Date.now() - 23 * 60 * 60 * 1000).toISOString(),
            rejectionReason: null,
            ipAddress: '192.168.1.102',
            deviceFingerprint: 'ghi789',
            balanceBefore: 6500.50,
            balanceAfter: 6300.50,
            riskScore: 0,
            isReversible: true,
            reversedAt: null,
            reversedBy: null
          },
          {
            id: 4,
            transactionId: 'TXN0000004',
            customerId: 3,
            customerName: 'Bob Johnson',
            customerEmail: 'bob.johnson@example.com',
            accountId: 3,
            accountNumber: 'ACC001234569',
            type: 'PAYMENT',
            amount: 1200.00,
            currency: 'USD',
            transactionDate: new Date(Date.now() - 10 * 60 * 1000).toISOString(), // 10 minutes ago
            description: 'Online bill payment - Electric Company',
            category: 'PAYMENT',
            status: 'PROCESSING',
            paymentMethod: 'ONLINE_BANKING',
            recipientAccount: null,
            recipientName: null,
            reference: 'ELEC001',
            isSuspicious: false,
            requiresApproval: false,
            approvedBy: null,
            approvedAt: null,
            rejectionReason: null,
            ipAddress: '192.168.1.103',
            deviceFingerprint: 'jkl012',
            balanceBefore: 9500.00,
            balanceAfter: 8300.00,
            riskScore: 0,
            isReversible: true,
            reversedAt: null,
            reversedBy: null
          },
          {
            id: 5,
            transactionId: 'TXN0000005',
            customerId: 3,
            customerName: 'Bob Johnson',
            customerEmail: 'bob.johnson@example.com',
            accountId: 3,
            accountNumber: 'ACC001234569',
            type: 'DEPOSIT',
            amount: 5000.00,
            currency: 'USD',
            transactionDate: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(), // 3 days ago
            description: 'Large cash deposit - flagged for review',
            category: 'DEPOSIT',
            status: 'REJECTED',
            paymentMethod: 'CASH',
            recipientAccount: null,
            recipientName: null,
            reference: 'CASH001',
            isSuspicious: true,
            requiresApproval: false,
            approvedBy: null,
            approvedAt: null,
            rejectionReason: 'Fraud suspicion',
            ipAddress: '192.168.1.104',
            deviceFingerprint: 'mno345',
            balanceBefore: 8300.00,
            balanceAfter: 8300.00,
            riskScore: 100,
            isReversible: false,
            reversedAt: null,
            reversedBy: null
          },
          // Crypto Transactions
          {
            id: 6,
            transactionId: 'TXN0000006',
            customerId: 4,
            customerName: 'Alice Crypto',
            customerEmail: 'alice.crypto@example.com',
            accountId: 4,
            accountNumber: 'ACC001234570',
            type: 'CRYPTO_BUY',
            amount: 2500.00,
            currency: 'USD',
            transactionDate: new Date(Date.now() - 1 * 60 * 60 * 1000).toISOString(), // 1 hour ago
            description: 'Bitcoin purchase - 0.05 BTC',
            category: 'CRYPTO',
            status: 'COMPLETED',
            paymentMethod: 'CRYPTO_EXCHANGE',
            recipientAccount: null,
            recipientName: null,
            reference: 'BTC001',
            isSuspicious: false,
            requiresApproval: false,
            approvedBy: 'admin1',
            approvedAt: new Date(Date.now() - 55 * 60 * 1000).toISOString(),
            rejectionReason: null,
            ipAddress: '192.168.1.105',
            deviceFingerprint: 'pqr678',
            balanceBefore: 10000.00,
            balanceAfter: 7500.00,
            riskScore: 15,
            isReversible: false,
            reversedAt: null,
            reversedBy: null
          },
          {
            id: 7,
            transactionId: 'TXN0000007',
            customerId: 4,
            customerName: 'Alice Crypto',
            customerEmail: 'alice.crypto@example.com',
            accountId: 4,
            accountNumber: 'ACC001234570',
            type: 'CRYPTO_SELL',
            amount: 1800.00,
            currency: 'USD',
            transactionDate: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(), // 2 hours ago
            description: 'Ethereum sale - 0.8 ETH',
            category: 'CRYPTO',
            status: 'PENDING',
            paymentMethod: 'CRYPTO_EXCHANGE',
            recipientAccount: null,
            recipientName: null,
            reference: 'ETH001',
            isSuspicious: false,
            requiresApproval: true,
            approvedBy: null,
            approvedAt: null,
            rejectionReason: null,
            ipAddress: '192.168.1.105',
            deviceFingerprint: 'pqr678',
            balanceBefore: 7500.00,
            balanceAfter: 9300.00,
            riskScore: 10,
            isReversible: true,
            reversedAt: null,
            reversedBy: null
          },
          {
            id: 8,
            transactionId: 'TXN0000008',
            customerId: 5,
            customerName: 'Charlie Crypto',
            customerEmail: 'charlie.crypto@example.com',
            accountId: 5,
            accountNumber: 'ACC001234571',
            type: 'CRYPTO_TRADE',
            amount: 500.00,
            currency: 'USD',
            transactionDate: new Date(Date.now() - 30 * 60 * 1000).toISOString(), // 30 minutes ago
            description: 'Crypto trading - BTC to ETH swap',
            category: 'CRYPTO_TRADING',
            status: 'COMPLETED',
            paymentMethod: 'CRYPTO_EXCHANGE',
            recipientAccount: null,
            recipientName: null,
            reference: 'SWAP001',
            isSuspicious: false,
            requiresApproval: false,
            approvedBy: 'admin2',
            approvedAt: new Date(Date.now() - 25 * 60 * 1000).toISOString(),
            rejectionReason: null,
            ipAddress: '192.168.1.106',
            deviceFingerprint: 'stu901',
            balanceBefore: 5000.00,
            balanceAfter: 5000.00,
            riskScore: 5,
            isReversible: false,
            reversedAt: null,
            reversedBy: null
          },
          // Trading Transactions
          {
            id: 9,
            transactionId: 'TXN0000009',
            customerId: 6,
            customerName: 'David Trader',
            customerEmail: 'david.trader@example.com',
            accountId: 6,
            accountNumber: 'ACC001234572',
            type: 'STOCK_BUY',
            amount: 3000.00,
            currency: 'USD',
            transactionDate: new Date(Date.now() - 45 * 60 * 1000).toISOString(), // 45 minutes ago
            description: 'Stock purchase - Apple Inc. (AAPL) - 15 shares',
            category: 'STOCK_TRADING',
            status: 'COMPLETED',
            paymentMethod: 'BROKERAGE',
            recipientAccount: null,
            recipientName: null,
            reference: 'AAPL001',
            isSuspicious: false,
            requiresApproval: false,
            approvedBy: 'admin1',
            approvedAt: new Date(Date.now() - 40 * 60 * 1000).toISOString(),
            rejectionReason: null,
            ipAddress: '192.168.1.107',
            deviceFingerprint: 'vwx234',
            balanceBefore: 15000.00,
            balanceAfter: 12000.00,
            riskScore: 8,
            isReversible: true,
            reversedAt: null,
            reversedBy: null
          },
          {
            id: 10,
            transactionId: 'TXN0000010',
            customerId: 6,
            customerName: 'David Trader',
            customerEmail: 'david.trader@example.com',
            accountId: 6,
            accountNumber: 'ACC001234572',
            type: 'STOCK_SELL',
            amount: 2500.00,
            currency: 'USD',
            transactionDate: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(), // 1 day ago
            description: 'Stock sale - Tesla Inc. (TSLA) - 10 shares',
            category: 'STOCK_TRADING',
            status: 'COMPLETED',
            paymentMethod: 'BROKERAGE',
            recipientAccount: null,
            recipientName: null,
            reference: 'TSLA001',
            isSuspicious: false,
            requiresApproval: false,
            approvedBy: 'admin2',
            approvedAt: new Date(Date.now() - 23 * 60 * 60 * 1000).toISOString(),
            rejectionReason: null,
            ipAddress: '192.168.1.107',
            deviceFingerprint: 'vwx234',
            balanceBefore: 12000.00,
            balanceAfter: 14500.00,
            riskScore: 12,
            isReversible: true,
            reversedAt: null,
            reversedBy: null
          },
          {
            id: 11,
            transactionId: 'TXN0000011',
            customerId: 7,
            customerName: 'Eva Investor',
            customerEmail: 'eva.investor@example.com',
            accountId: 7,
            accountNumber: 'ACC001234573',
            type: 'OPTION_BUY',
            amount: 800.00,
            currency: 'USD',
            transactionDate: new Date(Date.now() - 15 * 60 * 1000).toISOString(), // 15 minutes ago
            description: 'Options trading - Call option on SPY',
            category: 'OPTIONS_TRADING',
            status: 'PENDING',
            paymentMethod: 'BROKERAGE',
            recipientAccount: null,
            recipientName: null,
            reference: 'OPT001',
            isSuspicious: false,
            requiresApproval: true,
            approvedBy: null,
            approvedAt: null,
            rejectionReason: null,
            ipAddress: '192.168.1.108',
            deviceFingerprint: 'yza567',
            balanceBefore: 8000.00,
            balanceAfter: 7200.00,
            riskScore: 25,
            isReversible: true,
            reversedAt: null,
            reversedBy: null
          },
          // MT5 Transactions
          {
            id: 12,
            transactionId: 'TXN0000012',
            customerId: 8,
            customerName: 'Frank Forex',
            customerEmail: 'frank.forex@example.com',
            accountId: 8,
            accountNumber: 'ACC001234574',
            type: 'MT5_DEPOSIT',
            amount: 5000.00,
            currency: 'USD',
            transactionDate: new Date(Date.now() - 20 * 60 * 1000).toISOString(), // 20 minutes ago
            description: 'MT5 account deposit for forex trading',
            category: 'MT5_FOREX',
            status: 'COMPLETED',
            paymentMethod: 'MT5_PLATFORM',
            recipientAccount: null,
            recipientName: null,
            reference: 'MT5DEP001',
            isSuspicious: false,
            requiresApproval: false,
            approvedBy: 'admin1',
            approvedAt: new Date(Date.now() - 15 * 60 * 1000).toISOString(),
            rejectionReason: null,
            ipAddress: '192.168.1.109',
            deviceFingerprint: 'bcd890',
            balanceBefore: 20000.00,
            balanceAfter: 15000.00,
            riskScore: 18,
            isReversible: true,
            reversedAt: null,
            reversedBy: null
          },
          {
            id: 13,
            transactionId: 'TXN0000013',
            customerId: 8,
            customerName: 'Frank Forex',
            customerEmail: 'frank.forex@example.com',
            accountId: 8,
            accountNumber: 'ACC001234574',
            type: 'MT5_TRADE',
            amount: 1200.00,
            currency: 'USD',
            transactionDate: new Date(Date.now() - 10 * 60 * 1000).toISOString(), // 10 minutes ago
            description: 'MT5 forex trade - EUR/USD position',
            category: 'MT5_FOREX',
            status: 'COMPLETED',
            paymentMethod: 'MT5_PLATFORM',
            recipientAccount: null,
            recipientName: null,
            reference: 'MT5TRD001',
            isSuspicious: false,
            requiresApproval: false,
            approvedBy: 'admin2',
            approvedAt: new Date(Date.now() - 8 * 60 * 1000).toISOString(),
            rejectionReason: null,
            ipAddress: '192.168.1.109',
            deviceFingerprint: 'bcd890',
            balanceBefore: 15000.00,
            balanceAfter: 16200.00,
            riskScore: 22,
            isReversible: true,
            reversedAt: null,
            reversedBy: null
          },
          {
            id: 14,
            transactionId: 'TXN0000014',
            customerId: 9,
            customerName: 'Grace MT5',
            customerEmail: 'grace.mt5@example.com',
            accountId: 9,
            accountNumber: 'ACC001234575',
            type: 'MT5_WITHDRAWAL',
            amount: 3000.00,
            currency: 'USD',
            transactionDate: new Date(Date.now() - 5 * 60 * 1000).toISOString(), // 5 minutes ago
            description: 'MT5 account withdrawal',
            category: 'MT5_FOREX',
            status: 'PENDING',
            paymentMethod: 'MT5_PLATFORM',
            recipientAccount: null,
            recipientName: null,
            reference: 'MT5WTH001',
            isSuspicious: false,
            requiresApproval: true,
            approvedBy: null,
            approvedAt: null,
            rejectionReason: null,
            ipAddress: '192.168.1.110',
            deviceFingerprint: 'efg123',
            balanceBefore: 10000.00,
            balanceAfter: 7000.00,
            riskScore: 15,
            isReversible: true,
            reversedAt: null,
            reversedBy: null
          },
          // Additional Banking Transactions
          {
            id: 15,
            transactionId: 'TXN0000015',
            customerId: 10,
            customerName: 'Henry Bank',
            customerEmail: 'henry.bank@example.com',
            accountId: 10,
            accountNumber: 'ACC001234576',
            type: 'CREDIT',
            amount: 3500.00,
            currency: 'USD',
            transactionDate: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString(), // 3 hours ago
            description: 'Salary credit - Monthly payment',
            category: 'INCOME',
            status: 'COMPLETED',
            paymentMethod: 'DIRECT_DEPOSIT',
            recipientAccount: null,
            recipientName: null,
            reference: 'SALARY001',
            isSuspicious: false,
            requiresApproval: false,
            approvedBy: 'admin1',
            approvedAt: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
            rejectionReason: null,
            ipAddress: '192.168.1.111',
            deviceFingerprint: 'hij456',
            balanceBefore: 5000.00,
            balanceAfter: 8500.00,
            riskScore: 0,
            isReversible: true,
            reversedAt: null,
            reversedBy: null
          },
          {
            id: 16,
            transactionId: 'TXN0000016',
            customerId: 10,
            customerName: 'Henry Bank',
            customerEmail: 'henry.bank@example.com',
            accountId: 10,
            accountNumber: 'ACC001234576',
            type: 'DEBIT',
            amount: 150.00,
            currency: 'USD',
            transactionDate: new Date(Date.now() - 1 * 60 * 60 * 1000).toISOString(), // 1 hour ago
            description: 'Monthly service fee',
            category: 'FEES',
            status: 'COMPLETED',
            paymentMethod: 'AUTOMATIC',
            recipientAccount: null,
            recipientName: null,
            reference: 'FEE001',
            isSuspicious: false,
            requiresApproval: false,
            approvedBy: 'admin2',
            approvedAt: new Date(Date.now() - 55 * 60 * 1000).toISOString(),
            rejectionReason: null,
            ipAddress: '192.168.1.111',
            deviceFingerprint: 'hij456',
            balanceBefore: 8500.00,
            balanceAfter: 8350.00,
            riskScore: 0,
            isReversible: true,
            reversedAt: null,
            reversedBy: null
          }
        ],
        totalElements: 16,
        totalPages: 1
      };
    }

    if (endpoint.includes('/admin/audit-logs')) {
      return {
        content: [
          {
            id: 1,
            timestamp: new Date(Date.now() - 10 * 60 * 1000).toISOString(), // 10 minutes ago
            userId: 'admin1',
            username: 'super.admin',
            action: 'LOGIN',
            resource: 'ADMIN_PORTAL',
            resourceId: null,
            details: 'Admin user logged in successfully',
            ipAddress: '192.168.1.100',
            userAgent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36',
            status: 'SUCCESS',
            severity: 'INFO'
          },
          {
            id: 2,
            timestamp: new Date(Date.now() - 15 * 60 * 1000).toISOString(), // 15 minutes ago
            userId: 'admin2',
            username: 'customer.manager',
            action: 'UPDATE',
            resource: 'CUSTOMER',
            resourceId: '123',
            details: 'Updated customer status from ACTIVE to SUSPENDED',
            ipAddress: '192.168.1.101',
            userAgent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36',
            status: 'SUCCESS',
            severity: 'MEDIUM'
          },
          {
            id: 3,
            timestamp: new Date(Date.now() - 30 * 60 * 1000).toISOString(), // 30 minutes ago
            userId: 'admin3',
            username: 'compliance.officer',
            action: 'VIEW',
            resource: 'COMPLIANCE_ALERT',
            resourceId: 'ALT001',
            details: 'Viewed compliance alert for suspicious transaction',
            ipAddress: '192.168.1.102',
            userAgent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36',
            status: 'SUCCESS',
            severity: 'INFO'
          },
          {
            id: 4,
            timestamp: new Date(Date.now() - 45 * 60 * 1000).toISOString(), // 45 minutes ago
            userId: 'admin2',
            username: 'customer.manager',
            action: 'CREATE',
            resource: 'ACCOUNT',
            resourceId: 'ACC001234570',
            details: 'Created new savings account for customer ID 456',
            ipAddress: '192.168.1.101',
            userAgent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36',
            status: 'SUCCESS',
            severity: 'INFO'
          },
          {
            id: 5,
            timestamp: new Date(Date.now() - 60 * 60 * 1000).toISOString(), // 1 hour ago
            userId: 'admin1',
            username: 'super.admin',
            action: 'DELETE',
            resource: 'ADMIN_USER',
            resourceId: 'admin6',
            details: 'Deleted inactive admin user account',
            ipAddress: '192.168.1.100',
            userAgent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36',
            status: 'SUCCESS',
            severity: 'HIGH'
          },
          {
            id: 6,
            timestamp: new Date(Date.now() - 90 * 60 * 1000).toISOString(), // 1.5 hours ago
            userId: 'admin4',
            username: 'transaction.analyst',
            action: 'LOGIN_FAILED',
            resource: 'ADMIN_PORTAL',
            resourceId: null,
            details: 'Failed login attempt - invalid password',
            ipAddress: '192.168.1.103',
            userAgent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36',
            status: 'FAILED',
            severity: 'WARNING'
          },
          {
            id: 7,
            timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(), // 2 hours ago
            userId: 'admin3',
            username: 'compliance.officer',
            action: 'UPDATE',
            resource: 'COMPLIANCE_ALERT',
            resourceId: 'ALT002',
            details: 'Resolved compliance alert - marked as false positive',
            ipAddress: '192.168.1.102',
            userAgent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36',
            status: 'SUCCESS',
            severity: 'MEDIUM'
          },
          {
            id: 8,
            timestamp: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString(), // 3 hours ago
            userId: 'admin2',
            username: 'customer.manager',
            action: 'EXPORT',
            resource: 'CUSTOMER_REPORT',
            resourceId: null,
            details: 'Exported customer report for compliance review',
            ipAddress: '192.168.1.101',
            userAgent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36',
            status: 'SUCCESS',
            severity: 'INFO'
          },
          {
            id: 9,
            timestamp: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(), // 4 hours ago
            userId: 'admin1',
            username: 'super.admin',
            action: 'UPDATE',
            resource: 'SYSTEM_SETTINGS',
            resourceId: 'transaction_limits',
            details: 'Updated daily transaction limits configuration',
            ipAddress: '192.168.1.100',
            userAgent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36',
            status: 'SUCCESS',
            severity: 'HIGH'
          },
          {
            id: 10,
            timestamp: new Date(Date.now() - 5 * 60 * 60 * 1000).toISOString(), // 5 hours ago
            userId: 'admin5',
            username: 'read.only.user',
            action: 'VIEW',
            resource: 'DASHBOARD',
            resourceId: null,
            details: 'Accessed admin dashboard analytics',
            ipAddress: '192.168.1.104',
            userAgent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36',
            status: 'SUCCESS',
            severity: 'INFO'
          }
        ],
        totalElements: 10,
        totalPages: 1,
        pageable: {
          pageNumber: 0,
          pageSize: 50
        }
      };
    }

    return null;
  }

  // Dashboard Statistics
  async getDashboardStats(token?: string): Promise<ApiResponse<DashboardStats>> {
    return this.makeRequest<DashboardStats>('/admin/dashboard/stats', {}, token);
  }

  // Portfolio Statistics
  async getPortfolioStats(token?: string): Promise<ApiResponse<{
    totalCryptoValue: number;
    totalAssetValue: number;
    totalPortfolioValue: number;
    cryptoGrowth: number;
    assetGrowth: number;
  }>> {
    return this.makeRequest('/admin/dashboard/portfolio-stats', {}, token);
  }

  // Kucoin Balances
  async getKucoinBalances(accountId?: number | null, token?: string): Promise<ApiResponse<Record<string, Record<string, string>>>> {
    const url = accountId != null 
      ? `/admin/dashboard/kucoin-balances?accountId=${accountId}`
      : '/admin/dashboard/kucoin-balances';
    return this.makeRequest<Record<string, Record<string, string>>>(url, {}, token);
  }

  async getKucoinAccountOverview(currency: string = 'USDT', accountId?: number | null, token?: string): Promise<ApiResponse<{
    accountEquity: string;
    // Realised PnL for the futures account (if provided by the API)
    realisedPNL?: string;
    unrealisedPNL: string;
    marginBalance: string;
    positionMargin: string;
    orderMargin: string;
    frozenFunds: string;
    availableBalance: string;
    availableMargin: string;
    currency: string;
    riskRatio: string;
    maxWithdrawAmount: string;
  }>> {
    const url = accountId != null
      ? `/admin/dashboard/kucoin-account-overview?currency=${currency}&accountId=${accountId}`
      : `/admin/dashboard/kucoin-account-overview?currency=${currency}`;
    return this.makeRequest(url, {}, token);
  }

  async getKucoinFuturesHistoryPositions(limit: number = 200, accountId?: number | null, token?: string): Promise<ApiResponse<Array<{
    id?: string;
    symbol?: string;
    type?: string;
    side?: string;
    size?: string;
    pnl?: string;
    tradeFee?: string;
    fundingFee?: string;
    openTime?: number;
    closeTime?: number;
    openPrice?: string;
    closePrice?: string;
    leverage?: number;
    settleCurrency?: string;
    strategyName?: string;
  }>>> {
    const url = accountId != null
      ? `/admin/dashboard/kucoin-futures-history-positions?limit=${limit}&accountId=${accountId}`
      : `/admin/dashboard/kucoin-futures-history-positions?limit=${limit}`;
    return this.makeRequest(url, {}, token);
  }

  async getActiveTradingBots(token?: string): Promise<ApiResponse<ActiveTradingBotsResponse>> {
    return this.makeRequest<ActiveTradingBotsResponse>('/admin/dashboard/active-trading-bots', {}, token);
  }

  // Kucoin Snapshot Management
  async saveKucoinSnapshot(token?: string): Promise<ApiResponse<any>> {
    return this.makeRequest<any>('/api/admin/kucoin/snapshot', { method: 'POST' }, token);
  }

  async getPrimaryKucoinConfigId(token?: string): Promise<ApiResponse<number>> {
    return this.makeRequest<number>('/api/admin/kucoin/primary-config-id', {}, token);
  }

  async getKucoinSnapshots(
    startDate: string,
    endDate: string,
    accountId?: number | null,
    token?: string
  ): Promise<ApiResponse<any[]>> {
    const accountParam = accountId != null ? `&accountId=${accountId}` : '';
    return this.makeRequest<any[]>(
      `/api/admin/kucoin/snapshots?startDate=${startDate}&endDate=${endDate}${accountParam}`,
      {},
      token
    );
  }

  async getKucoinSnapshotByDate(date: string, token?: string): Promise<ApiResponse<any>> {
    return this.makeRequest<any>(`/api/admin/kucoin/snapshot/${date}`, {}, token);
  }

  async getLatestKucoinSnapshot(token?: string): Promise<ApiResponse<any>> {
    return this.makeRequest<any>('/api/admin/kucoin/snapshot/latest', {}, token);
  }

  async getKucoinFundingHistory(currency: string = 'USDT', token?: string): Promise<ApiResponse<any>> {
    return this.makeRequest<any>(`/api/admin/kucoin/funding-history?currency=${currency}`, {}, token);
  }

  // Customer Management
  async getAllCustomers(
    page: number = 0,
    size: number = 100,
    searchTerm?: string,
    status?: string,
    sortBy?: string,
    sortDirection?: string,
    token?: string
  ): Promise<ApiResponse<{ content: AdminCustomer[]; totalElements: number; totalPages: number }>> {
    const params = new URLSearchParams({
      page: page.toString(),
      size: size.toString(),
      ...(searchTerm && { search: searchTerm }),
      ...(status && { status }),
      ...(sortBy && { sortBy }),
      ...(sortDirection && { sortDirection })
    });

    return this.makeRequest<{ content: AdminCustomer[]; totalElements: number; totalPages: number }>(
      `/admin/customers?${params}`,
      {},
      token
    );
  }

  async getCustomerById(customerId: number, token?: string): Promise<ApiResponse<AdminCustomer>> {
    return this.makeRequest<AdminCustomer>(`/admin/customers/${customerId}`, {}, token);
  }

  async suspendCustomer(customerId: number, reason: string, token?: string): Promise<ApiResponse<void>> {
    return this.makeRequest<void>(`/admin/customers/${customerId}/suspend`, {
      method: 'POST',
      body: JSON.stringify({ reason })
    }, token);
  }

  async reactivateCustomer(customerId: number, reason: string, token?: string): Promise<ApiResponse<void>> {
    return this.makeRequest<void>(`/admin/customers/${customerId}/reactivate`, {
      method: 'POST',
      body: JSON.stringify({ reason })
    }, token);
  }

  async getRealCustomersWithAccounts(
    page: number = 0,
    size: number = 100,
    token?: string,
    sortBy?: string,
    sortDirection?: string
  ): Promise<ApiResponse<any>> {
    const params = new URLSearchParams({
      page: page.toString(),
      size: size.toString(),
      ...(sortBy && { sortBy }),
      ...(sortDirection && { sortDirection })
    });

    return this.makeRequest<any>(`/admin/customers/paged?${params}`, {}, token);
  }

  // NOTE: To fully migrate to Redux token, update other methods to accept a token and pass it to makeRequest as shown above.
  // Account Management
  async getAllAccounts(
    page: number = 0,
    size: number = 100,
    searchTerm?: string,
    status?: string,
    type?: string,
    sortBy?: string,
    sortDirection?: string,
    token?: string
  ): Promise<ApiResponse<{ content: AdminAccount[]; totalElements: number; totalPages: number }>> {
    const params = new URLSearchParams({
      page: page.toString(),
      size: size.toString(),
      ...(searchTerm && { search: searchTerm }),
      ...(status && { status }),
      ...(type && { type }),
      ...(sortBy && { sortBy }),
      ...(sortDirection && { sortDirection })
    });

    return this.makeRequest<{ content: AdminAccount[]; totalElements: number; totalPages: number }>(
      `/admin/accounts?${params}`,
      {},
      token
    );
  }

  async freezeAccount(accountId: number, reason: string, token?: string): Promise<ApiResponse<void>> {
    return this.makeRequest<void>(`/admin/accounts/${accountId}/freeze`, {
      method: 'POST',
      body: JSON.stringify({ reason })
    }, token);
  }

  async unfreezeAccount(accountId: number, reason: string, token?: string): Promise<ApiResponse<void>> {
    return this.makeRequest<void>(`/admin/accounts/${accountId}/unfreeze`, {
      method: 'POST',
      body: JSON.stringify({ reason })
    }, token);
  }

  async adjustAccountBalance(accountId: number, newBalance: number, reason: string, token?: string): Promise<ApiResponse<void>> {
    return this.makeRequest<void>(`/admin/accounts/${accountId}/adjust-balance`, {
      method: 'POST',
      body: JSON.stringify({ newBalance, reason })
    }, token);
  }

  // Transaction Management
  async getTransactions(filter: TransactionFilter, token?: string): Promise<ApiResponse<{ content: AdminTransaction[]; totalElements: number; totalPages: number }>> {
    const params = new URLSearchParams({
      page: filter.page?.toString() ?? '0',
      size: filter.size?.toString() ?? '100',
      ...(filter.sortBy && { sortBy: filter.sortBy }),
      ...(filter.sortDirection && { sortDirection: filter.sortDirection }),
      ...(filter.status && { status: filter.status }),
      ...(filter.type && { type: filter.type }),
      ...(filter.customerId && { customerId: filter.customerId.toString() }),
      ...(filter.fromDate && { startDate: filter.fromDate }),
      ...(filter.toDate && { endDate: filter.toDate }),
    });
    
    const response = await this.makeRequest<{ content: AdminTransaction[]; totalElements: number; totalPages: number }>(
      `/admin/transactions?${params.toString()}`,
      {},
      token
    );

    // Temporary workaround: If no banking transactions are returned, add mock banking data
    if (response.success && response.data && response.data.content) {
      const hasBankingTransactions = response.data.content.some(t => 
        ['DEPOSIT', 'WITHDRAWAL', 'TRANSFER', 'PAYMENT', 'CREDIT', 'DEBIT'].includes(t.type)
      );
      
      if (!hasBankingTransactions) {
        // Add mock banking transactions to the response
        const mockBankingTransactions: AdminTransaction[] = [
          {
            id: 101,
            transactionId: 'TXN0000101',
            customerId: 1,
            customerName: 'John Doe',
            customerEmail: 'john.doe@example.com',
            accountId: 1,
            accountNumber: 'ACC001234567',
            type: 'DEBIT',
            amount: 1.46,
            currency: 'USD',
            transactionDate: new Date('2025-07-07T10:04:33.305275Z').toISOString(),
            description: 'Payment to 0131611553',
            category: 'Payment',
            status: 'COMPLETED',
            paymentMethod: 'ONLINE',
            recipientAccount: null,
            recipientName: null,
            reference: null,
            isSuspicious: false,
            requiresApproval: false,
            approvedBy: 'admin1',
            approvedAt: new Date('2025-07-07T10:04:33.305275Z').toISOString(),
            rejectionReason: null,
            ipAddress: '192.168.1.100',
            deviceFingerprint: 'abc123',
            balanceBefore: 5000.50,
            balanceAfter: 4999.04,
            riskScore: 0,
            isReversible: true,
            reversedAt: null,
            reversedBy: null
          },
          {
            id: 102,
            transactionId: 'TXN0000102',
            customerId: 3,
            customerName: 'Bob Johnson',
            customerEmail: 'bob.johnson@example.com',
            accountId: 3,
            accountNumber: 'ACC001234569',
            type: 'CREDIT',
            amount: 1.46,
            currency: 'USD',
            transactionDate: new Date('2025-07-07T10:04:33.359534Z').toISOString(),
            description: 'Payment from 1955738130',
            category: 'Payment',
            status: 'COMPLETED',
            paymentMethod: 'ONLINE',
            recipientAccount: null,
            recipientName: null,
            reference: null,
            isSuspicious: false,
            requiresApproval: false,
            approvedBy: 'admin1',
            approvedAt: new Date('2025-07-07T10:04:33.359534Z').toISOString(),
            rejectionReason: null,
            ipAddress: '192.168.1.101',
            deviceFingerprint: 'def456',
            balanceBefore: 8500.25,
            balanceAfter: 8501.71,
            riskScore: 0,
            isReversible: true,
            reversedAt: null,
            reversedBy: null
          },
          {
            id: 103,
            transactionId: 'TXN0000103',
            customerId: 1,
            customerName: 'John Doe',
            customerEmail: 'john.doe@example.com',
            accountId: 1,
            accountNumber: 'ACC001234567',
            type: 'DEBIT',
            amount: 56.00,
            currency: 'USD',
            transactionDate: new Date('2025-07-07T10:12:04.483670Z').toISOString(),
            description: 'Payment to 0131611553',
            category: 'Payment',
            status: 'COMPLETED',
            paymentMethod: 'ONLINE',
            recipientAccount: null,
            recipientName: null,
            reference: null,
            isSuspicious: false,
            requiresApproval: false,
            approvedBy: 'admin1',
            approvedAt: new Date('2025-07-07T10:12:04.483670Z').toISOString(),
            rejectionReason: null,
            ipAddress: '192.168.1.100',
            deviceFingerprint: 'abc123',
            balanceBefore: 4999.04,
            balanceAfter: 4943.04,
            riskScore: 0,
            isReversible: true,
            reversedAt: null,
            reversedBy: null
          }
        ];
        
        // Combine real transactions with mock banking transactions
        response.data.content = [...response.data.content, ...mockBankingTransactions];
        response.data.totalElements = response.data.content.length;
      }
    }
    
    return response;
  }

  async approveTransaction(transactionId: number, token?: string): Promise<ApiResponse<void>> {
    return this.makeRequest(`/admin/transactions/${transactionId}/approve`, {
      method: 'POST'
    }, token);
  }

  async rejectTransaction(transactionId: number, reason: string, token?: string): Promise<ApiResponse<void>> {
    return this.makeRequest(`/admin/transactions/${transactionId}/reject`, {
      method: 'POST',
      body: JSON.stringify({ reason })
    }, token);
  }

  async createManualTransaction(transactionData: any, token?: string): Promise<ApiResponse<AdminTransaction>> {
    return this.makeRequest<AdminTransaction>('/admin/transactions/manual', {
      method: 'POST',
      body: JSON.stringify(transactionData)
    }, token);
  }

  // Compliance & Risk Management
  async getComplianceAlerts(
    page: number = 0,
    size: number = 100,
    severity?: string,
    status?: string,
    productId?: number,
    token?: string
  ): Promise<ApiResponse<{ content: ComplianceAlert[]; totalElements: number; totalPages: number }>> {
    console.log('getComplianceAlerts called with params:', { page, size, severity, status, productId });
    console.trace('getComplianceAlerts call stack');
    
    const params = new URLSearchParams({
      page: page.toString(),
      size: size.toString(),
      ...(severity && { severity }),
      ...(status && { status }),
      ...(productId && { productId: productId.toString() })
    });

    return this.makeRequest<{ content: ComplianceAlert[]; totalElements: number; totalPages: number }>(
      `/admin/compliance/alerts?${params}`,
      {},
      token
    );
  }

  async assignAlert(alertId: string, assigneeId: string, token?: string): Promise<ApiResponse<void>> {
    return this.makeRequest<void>(`/admin/compliance/alerts/${alertId}/assign`, {
      method: 'POST',
      body: JSON.stringify({ assigneeId })
    }, token);
  }

  async updateAlertStatus(alertId: string, status: string, notes?: string, token?: string): Promise<ApiResponse<void>> {
    return this.makeRequest<void>(`/admin/compliance/alerts/${alertId}/status`, {
      method: 'PUT',
      body: JSON.stringify({ status, notes })
    }, token);
  }

  async resolveComplianceAlert(
    alertId: string,
    resolution: 'APPROVED' | 'REJECTED' | 'REQUIRES_ADDITIONAL_INFO' | 'DISMISSED',
    actionTaken: string,
    token?: string
  ): Promise<ApiResponse<void>> {
    return this.makeRequest<void>(
      `/admin/compliance/alerts/${alertId}/resolve`,
      { 
        method: 'POST',
        body: JSON.stringify({ resolution, actionTaken })
      },
      token
    );
  }

  // Retail User Management
  async getAllRetailUsers(token?: string): Promise<ApiResponse<any[]>> {
    try {
      // Helper function to map user data to frontend format
      const mapUserData = (content: any[]): any[] => {
        return (Array.isArray(content) ? content : []).map(user => ({
          id: user.id.toString(),
          username: user.username,
          email: user.email,
          firstName: user.firstName,
          lastName: user.lastName,
          companyName: user.companyName,
          userType: user.userType || 'RETAIL',
          riskCategory: user.riskCategory || 'NORMAL',
          status: user.status ? 'ACTIVE' : 'INACTIVE',
          phone: user.phone,
          role: 'RETAIL_USER', // Default role for retail users
          department: 'RETAIL', // Default department
          permissions: user.permissions || [], // Permissions are now stored in database
          lastLogin: user.lastLoginAt,
          createdDate: user.createdAt,
          createdBy: 'SYSTEM',
          updatedDate: user.updatedAt,
          updatedBy: user.updatedBy,
          avatarBase64: user.avatarBase64,
          isActive: user.status,
          mfaEnabled: false, // Retail users don't have MFA by default
          product: user.product ? {
            id: user.product.id,
            name: user.product.name,
            type: user.product.type
          } : null
        }));
      };

      // Helper function to extract content from response
      const extractContent = (responseData: any): any[] => {
        // If the response has a content property, use it (paginated response)
        if (responseData.content && Array.isArray(responseData.content)) {
          return responseData.content;
        }
        // If the response is directly an array, use it
        else if (Array.isArray(responseData)) {
          return responseData;
        }
        // Otherwise, try to extract from nested structure
        else if (responseData.data && Array.isArray(responseData.data)) {
          return responseData.data;
        }
        return [];
      };

      // Fetch first page to get total pages information
      const firstPageResponse = await this.makeRequest<any>(
        '/admin/customers/all?page=0&size=100&sortBy=id&sortDirection=desc',
        {},
        token
      );
      
      console.log('getAllRetailUsers first page response:', firstPageResponse);
      
      if (!firstPageResponse.success || !firstPageResponse.data) {
      return { success: false, error: 'No data received' };
      }

      let allUsers: any[] = [];
      const firstPageContent = extractContent(firstPageResponse.data);
      allUsers = allUsers.concat(firstPageContent);
      
      // Check if there are more pages to fetch
      let totalPages = 1;
      
      // Try to get pagination info from response
      if (firstPageResponse.data.totalPages !== undefined) {
        totalPages = firstPageResponse.data.totalPages;
      } else if (firstPageResponse.data.totalElements !== undefined && firstPageResponse.data.size !== undefined) {
        // Calculate total pages from totalElements and size
        const pageSize = firstPageResponse.data.size || 100;
        const totalElements = firstPageResponse.data.totalElements;
        totalPages = Math.ceil(totalElements / pageSize);
      }
      
      console.log(`Total pages to fetch: ${totalPages}, First page loaded: ${firstPageContent.length} users`);
      
      // Fetch remaining pages if there are more
      if (totalPages > 1) {
        const remainingPagePromises: Promise<any>[] = [];
        
        for (let page = 1; page < totalPages; page++) {
          remainingPagePromises.push(
            this.makeRequest<any>(
              `/admin/customers/all?page=${page}&size=100&sortBy=id&sortDirection=desc`,
              {},
              token
            )
          );
        }
        
        // Fetch all remaining pages in parallel
        const remainingResponses = await Promise.all(remainingPagePromises);
        
        // Extract content from all remaining pages
        remainingResponses.forEach((response, index) => {
          if (response.success && response.data) {
            const pageContent = extractContent(response.data);
            allUsers = allUsers.concat(pageContent);
            console.log(`Page ${index + 2} loaded: ${pageContent.length} users`);
          }
        });
      }
      
      console.log(`Total retail users loaded: ${allUsers.length}`);
      
      // Map all users to frontend format
      const mappedUsers = mapUserData(allUsers);
      
      console.log('Mapped retail users:', mappedUsers.length);
      return { success: true, data: mappedUsers };
      
    } catch (error) {
      console.error('Error fetching retail users:', error);
      return { success: false, error: error instanceof Error ? error.message : 'Failed to fetch retail users' };
    }
  }

  // Retail User Management
  async updateRetailUser(userId: string, updateData: any, token?: string): Promise<ApiResponse<any>> {
    try {
      const response = await this.makeRequest<any>(`/admin/customers/${userId}`, {
        method: 'PUT',
        body: JSON.stringify(updateData)
      }, token);
      
      console.log('updateRetailUser response:', response);
      return response;
    } catch (error) {
      console.error('Error updating retail user:', error);
      return { success: false, error: error instanceof Error ? error.message : 'Failed to update retail user' };
    }
  }

  async toggleRetailUserStatus(userId: string, token?: string): Promise<ApiResponse<any>> {
    try {
      const response = await this.makeRequest<any>(`/admin/customers/${userId}/toggle-status`, {
        method: 'POST'
      }, token);
      
      console.log('toggleRetailUserStatus response:', response);
      return response;
    } catch (error) {
      console.error('Error toggling retail user status:', error);
      return { success: false, error: error instanceof Error ? error.message : 'Failed to toggle retail user status' };
    }
  }

  // Update retail user permissions
  async updateRetailUserPermissions(userId: string, permissions: string[], token?: string): Promise<ApiResponse<any>> {
    try {
      const response = await this.makeRequest<any>(`/admin/customers/${userId}/permissions`, {
        method: 'PUT',
        body: JSON.stringify(permissions)
      }, token);
      return response;
    } catch (error) {
      console.error('Error updating retail user permissions:', error);
      return { success: false, error: error instanceof Error ? error.message : 'Failed to update retail user permissions' };
    }
  }

  // Admin User Management
  async getAllAdminUsers(token?: string): Promise<ApiResponse<any[]>> {
    try {
      const response = await this.makeRequest<any>('/admin/users', {}, token);
      
      console.log('getAllAdminUsers response:', response);
      
      // Map backend response to frontend format
      if (response.success && response.data) {
        // Handle paginated response - extract content array from Page object
        // The backend returns: { data: { content: [...] } }
        let content = response.data;
        
        console.log('Raw response.data:', response.data);
        
        // If the response has a content property, use it (paginated response)
        if (response.data.content && Array.isArray(response.data.content)) {
          content = response.data.content;
          console.log('Using response.data.content:', content);
        }
        // If the response is directly an array, use it
        else if (Array.isArray(response.data)) {
          content = response.data;
          console.log('Using response.data directly:', content);
        }
        // Otherwise, try to extract from nested structure
        else if (response.data.data && Array.isArray(response.data.data)) {
          content = response.data.data;
          console.log('Using response.data.data:', content);
        }
        
        const mappedUsers = (Array.isArray(content) ? content : []).map(user => ({
          id: user.id.toString(),
          username: user.username,
          email: user.email,
          firstName: user.firstName,
          lastName: user.lastName,
          role: user.role,
          department: user.department,
          status: user.isActive ? 'ACTIVE' : 'INACTIVE',
          permissions: user.permissions || [],
          lastLogin: user.lastLoginAt,
          createdDate: user.createdAt,
          createdBy: user.createdBy,
          assignedProducts: user.assignedProducts || [],
          // SEPA & Risk fields
          userType: user.userType,
          riskCategory: user.riskCategory,
          reviewRequired: user.reviewRequired,
          sepaDailyLimit: user.sepaDailyLimit,
          sepaMonthlyLimit: user.sepaMonthlyLimit,
          sepaYearlyLimit: user.sepaYearlyLimit,
          sepaSingleTransactionLimit: user.sepaSingleTransactionLimit,
          sepaLimitCurrency: user.sepaLimitCurrency,
        }));
        
        console.log('Mapped users:', mappedUsers);
        
        return {
          data: mappedUsers,
          success: true
        };
      }
      return {
        data: [],
        success: false,
        error: response.error || 'No data received from server'
      };
    } catch (error) {
      console.error('Failed to fetch admin users:', error);
      return {
        data: [], // Return empty array instead of null
        success: false,
        error: error instanceof Error ? error.message : 'Failed to fetch admin users'
      };
    }
  }

  // Update admin user (SEPA/risk fields)
  async updateAdminUser(userId: string, updateData: Partial<any>, token?: string): Promise<ApiResponse<any>> {
    try {
      const response = await this.makeRequest(`/admin/users/${userId}`, {
        method: 'PUT',
        body: JSON.stringify(updateData)
      }, token);
      return response;
    } catch (error) {
      console.error('Failed to update admin user:', error);
      return {
        data: null,
        success: false,
        error: error instanceof Error ? error.message : 'Failed to update admin user'
      };
    }
  }

  // Admin Profile Management
  async getAdminProfile(token?: string): Promise<ApiResponse<any>> {
    try {
      const response = await this.makeRequest<any>('/admin/profile', {}, token);
      
      if (response.success && response.data) {
        // Map backend response to frontend format
        const profileData = {
          id: response.data.id,
          username: response.data.username,
          email: response.data.email,
          firstName: response.data.firstName,
          lastName: response.data.lastName,
          fullName: `${response.data.firstName} ${response.data.lastName}`.trim(),
          role: response.data.role,
          department: response.data.department,
          permissions: response.data.permissions || [],
          isActive: response.data.isActive,
          createdAt: response.data.createdAt,
          lastLoginAt: response.data.lastLoginAt,
          avatarBase64: response.data.avatarBase64,
          assignedProducts: response.data.assignedProducts || []
        };
        return {
          data: profileData,
          success: true
        };
      }
      return response;
    } catch (error) {
      console.error('Failed to fetch admin profile:', error);
      return {
        data: null,
        success: false,
        error: error instanceof Error ? error.message : 'Failed to fetch admin profile'
      };
    }
  }

  async uploadAdminAvatar(file: File, token?: string): Promise<ApiResponse<any>> {
    try {
      const formData = new FormData();
      formData.append('file', file);

      // Determine if we should use JWT or session auth
      const headers: Record<string, string> = {};
      
      // Only add Authorization header for JWT auth (when token is provided and not null)
      if (token && token !== 'null' && token !== 'undefined') {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${this.baseUrl}/admin/profile/avatar`, {
        method: 'POST',
        headers,
        credentials: 'include', // Always include credentials for session auth
        body: formData
      });

      const data = await response.json();
      
      if (response.ok && data.success) {
        // Map backend response to frontend format
        const profileData = {
          id: data.data.id,
          username: data.data.username,
          email: data.data.email,
          firstName: data.data.firstName,
          lastName: data.data.lastName,
          fullName: `${data.data.firstName} ${data.data.lastName}`.trim(),
          role: data.data.role,
          department: data.data.department,
          permissions: data.data.permissions || [],
          isActive: data.data.isActive,
          createdAt: data.data.createdAt,
          lastLoginAt: data.data.lastLoginAt,
          avatarBase64: data.data.avatarBase64
        };
        return {
          data: profileData,
          success: true
        };
      }
      
      return {
        data: null,
        success: false,
        error: data.message || 'Failed to upload avatar'
      };
    } catch (error) {
      console.error('Failed to upload admin avatar:', error);
      return {
        data: null,
        success: false,
        error: error instanceof Error ? error.message : 'Failed to upload avatar'
      };
    }
  }

  async changeAdminPassword(oldPassword: string, newPassword: string, token?: string): Promise<ApiResponse<void>> {
    try {
      const response = await this.makeRequest<any>(
        '/admin/profile/change-password',
        {
          method: 'PUT',
          body: JSON.stringify({
            oldPassword,
            newPassword
          })
        },
        token
      );

      if (response.success) {
        return {
          data: undefined,
          success: true
        };
      }

      // Handle backend error response
      if (response.data && typeof response.data === 'object' && 'message' in response.data) {
        const backendResponse = response.data as any;
        return {
          data: undefined,
          success: false,
          error: backendResponse.message || 'Failed to change password'
        };
      }

      return {
        data: undefined,
        success: false,
        error: response.error || 'Failed to change password'
      };
    } catch (error: any) {
      console.error('Failed to change admin password:', error);
      
      // Extract error message from response if available
      let errorMessage = 'Failed to change password';
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.errors && Array.isArray(error.response.data.errors)) {
        errorMessage = error.response.data.errors.join(', ');
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      return {
        data: undefined,
        success: false,
        error: errorMessage
      };
    }
  }

  async createAdminUser(userData: any, token?: string): Promise<ApiResponse<any>> {
    try {
      // Map frontend data to backend format
      const backendData = {
        username: userData.username,
        email: userData.email,
        firstName: userData.firstName,
        lastName: userData.lastName,
        role: userData.role,
        department: userData.department,
        permissions: userData.permissions,
        password: userData.password // Make sure to collect this from the form
      };

      const response = await this.makeRequest('/admin/users', {
        method: 'POST',
        body: JSON.stringify(backendData)
      }, token);

      return response;
    } catch (error) {
      console.error('Failed to create admin user:', error);
      return {
        data: null,
        success: false,
        error: error instanceof Error ? error.message : 'Failed to create admin user'
      };
    }
  }

  async createRetailUser(data: { username: string; firstName: string; lastName: string; email: string; phone?: string; userType?: string; riskCategory?: string; password: string }, token?: string): Promise<ApiResponse<any>> {
    try {
      const response = await this.makeRequest('/admin/customers', {
        method: 'POST',
        body: JSON.stringify(data)
      }, token);
      return response;
    } catch (error) {
      console.error('Failed to create retail user:', error);
      return { success: false, data: null, error: error instanceof Error ? error.message : 'Failed to create retail user' };
    }
  }

  async toggleAdminUserStatus(userId: string, token?: string): Promise<ApiResponse<any>> {
    try {
      const response = await this.makeRequest(`/admin/users/${userId}/toggle-status`, {
        method: 'POST'
      }, token);

      return response;
    } catch (error) {
      console.error('Failed to toggle admin user status:', error);
      return {
        data: null,
        success: false,
        error: error instanceof Error ? error.message : 'Failed to toggle admin user status'
      };
    }
  }
  
  async updateAdminUserPermissions(userId: string, permissions: string[], token?: string): Promise<ApiResponse<any>> {
    return this.makeRequest(`/admin/users/${userId}/permissions`, {
      method: 'PUT',
      body: JSON.stringify({ permissions })
    }, token);
  }

  // Audit & Reporting
  async getAuditLogs(
    page: number = 0,
    size: number = 50,
    filters?: {
      action?: string;
      actionPrefix?: string;
      entityType?: string;
      entityId?: number;
      aiTuneOnly?: boolean;
      marketSignalOnly?: boolean;
    },
    token?: string
  ): Promise<ApiResponse<any>> {
    const params = new URLSearchParams();
    params.set('page', String(page));
    params.set('size', String(size));
    if (filters?.action) params.set('action', filters.action);
    if (filters?.actionPrefix) params.set('actionPrefix', filters.actionPrefix);
    if (filters?.entityType) params.set('entityType', filters.entityType);
    if (filters?.entityId != null) params.set('entityId', String(filters.entityId));
    if (filters?.aiTuneOnly) params.set('aiTuneOnly', 'true');
    if (filters?.marketSignalOnly) params.set('marketSignalOnly', 'true');
    return this.makeRequest<any>(`/admin/audit-logs?${params.toString()}`, {}, token);
  }

  async exportCustomerReport(filters: any, token?: string): Promise<ApiResponse<Blob>> {
    return this.makeRequest<Blob>('/admin/reports/customers/export', {
      method: 'POST',
      body: JSON.stringify(filters)
    }, token);
  }

  async exportTransactionReport(filters: any, token?: string): Promise<ApiResponse<Blob>> {
    return this.makeRequest<Blob>('/admin/reports/transactions/export', {
      method: 'POST',
      body: JSON.stringify(filters)
    }, token);
  }

  async exportComplianceReport(filters: any, token?: string): Promise<ApiResponse<Blob>> {
    return this.makeRequest<Blob>('/admin/reports/compliance/export', {
      method: 'POST',
      body: JSON.stringify(filters)
    }, token);
  }

  // Trading Strategy Reports
  async getTradingStrategyDetailedTransactions(token?: string): Promise<ApiResponse<any[]>> {
    return this.makeRequest<any[]>('/api/admin/trading-strategies/reports/transactions/detailed', {}, token);
  }

  async getTradingStrategySummary(token?: string): Promise<ApiResponse<any[]>> {
    return this.makeRequest<any[]>('/api/admin/trading-strategies/reports/summary', {}, token);
  }

  async getTradingStrategyPnlSummary(token?: string): Promise<ApiResponse<any[]>> {
    return this.makeRequest<any[]>('/api/admin/trading-strategies/reports/pnl-summary', {}, token);
  }

  async getTradingStrategyPnlHistory(daysBack: number = 30, token?: string): Promise<ApiResponse<any[]>> {
    return this.makeRequest<any[]>(`/api/admin/trading-strategies/reports/pnl-history?daysBack=${daysBack}`, {}, token);
  }

  async getTradingStrategyComparison(token?: string): Promise<ApiResponse<any[]>> {
    return this.makeRequest<any[]>('/api/admin/trading-strategies/reports/trading-type-comparison', {}, token);
  }

  async getTradingStrategyRecentTransactions(days: number = 30, token?: string): Promise<ApiResponse<any[]>> {
    return this.makeRequest<any[]>(`/api/admin/trading-strategies/reports/transactions/recent?days=${days}`, {}, token);
  }

  async getTradingStrategyDetailedTransactionsWithUsers(token?: string): Promise<ApiResponse<any[]>> {
    return this.makeRequest<any[]>(`/api/admin/trading-strategies/reports/transactions/detailed-with-users`, {
      method: 'GET',
    }, token);
  }

  // Volatility Data
  async getTradingStrategyVolatilityData(marketType: string = 'FUTURES', token?: string): Promise<ApiResponse<any[]>> {
    return this.makeRequest<any[]>(`/api/admin/trading-strategies/volatility-data?marketType=${marketType}`, {
      method: 'GET',
    }, token);
  }

  // Update Take Profit + full strategy config for a trading strategy
  async updateStrategyTakeProfit(
    strategyId: number,
    values: {
      // Take Profit
      takeProfit?: number;
      basketTakeProfit?: number;
      longTakeProfit?: number;
      shortTakeProfit?: number;
      // Stop Loss
      stopLossType?: 'CLASSIC' | 'TRAILING';
      trailingStopLoss?: number;
      longTrailingStopLoss?: number;
      shortTrailingStopLoss?: number;
      // Micro Trailing Stop
      enableMicroTsl?: boolean;
      microTslActivationPercent?: number;
      microTslDistancePercent?: number;
      // Market & Positions
      maxOpenPositions?: number;
      leverage?: number;
      fixedOrderAmount?: number;
      // BTC Correlation Filter
      btcCorrelationEnabled?: boolean;
      btcEmaLength?: number;
      btcEmaTimeframe?: string;
      btcMomentumLongThreshold?: number;
      btcMomentumShortThreshold?: number;
      btcRangeNoTradeThreshold?: number;
      // Dump Protection
      btcDumpThreshold?: number;
      btcCooldownMinutes?: number;
      // BTC Higher-Timeframe Trend Confirmation
      btcTrendTimeframe?: string;
      btcTrendEmaPeriod?: number;
      // Minimum Profit Exit
      minProfitExitPercent?: number;
      // Order Type
      orderType?: 'MARKET' | 'LIMIT';
      limitOffset?: number;
      limitTimeoutSeconds?: number;
      // Margin Mode
      marginMode?: 'CROSS' | 'ISOLATED';
      // Meme Participation Filter
      memeParticipationFilterEnabled?: boolean;
      minParticipationRate?: number;
      // Market Activity Threshold (0 = disabled)
      minMarketActivityThreshold?: number;
      // Volatility Filter
      enableVolatilityFilter?: boolean;
      minMarketVolatility?: number;
      maxMarketVolatility?: number;
      volatilityCalculationCoins?: number;
      volatilityCalculationMethod?: 'HIGH_LOW_RANGE' | 'CHANGE_24H' | 'AUTO' | string;
      // Coin Selection Oracle
      oracleEnabled?: boolean;
      oracleConfidenceThreshold?: number;
      oracleWeightMomentum?: number | null;
      oracleWeightRsi?: number | null;
      oracleWeightVolume?: number | null;
      oracleWeightFunding?: number | null;
      oracleWeightEma?: number | null;
    },
    token?: string
  ): Promise<ApiResponse<any>> {
    try {
      const response = await axiosInstance.patch(
        `/api/admin/trading-strategies/${strategyId}/config/take-profit`,
        values,
        {
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {})
          }
        }
      );
      return {
        success: true,
        data: response.data.data || response.data
      };
    } catch (error: any) {
      console.error('Error updating Take Profit:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.response?.data?.error || error.message || 'Failed to update Take Profit'
      };
    }
  }

  // Update Entry Filters config for a Meme Coin Pairs trading strategy
  async updateStrategyEntryFilters(
    strategyId: number,
    values: {
      entryFiltersEnabled?: boolean;
      fundingRateFilterEnabled?: boolean;
      rsiFilterEnabled?: boolean;
      emaFilterEnabled?: boolean;
      maxFundingRateLong?: number;
      rsiPeriod?: number;
      rsiCandleLimit?: number;
      rsiLongThreshold?: number;
      rsiShortThreshold?: number;
      emaPeriod?: number;
    },
    token?: string
  ): Promise<ApiResponse<any>> {
    try {
      const response = await axiosInstance.patch(
        `/api/admin/trading-strategies/${strategyId}/config/entry-filters`,
        values,
        {
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {})
          }
        }
      );
      return {
        success: true,
        data: response.data.data || response.data
      };
    } catch (error: any) {
      console.error('Error updating Entry Filters:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.response?.data?.error || error.message || 'Failed to update Entry Filters'
      };
    }
  }

  async updateStrategyFilterProfile(
    strategyId: number,
    filterProfileId: number | null,
    token?: string
  ): Promise<ApiResponse<any>> {
    try {
      const response = await axiosInstance.patch(
        `/api/admin/trading-strategies/${strategyId}/filter-profile`,
        { filterProfileId },
        {
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {})
          }
        }
      );
      return {
        success: true,
        data: response.data.data || response.data
      };
    } catch (error: any) {
      console.error('Error updating strategy filter profile:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.response?.data?.error || error.message || 'Failed to update filter profile'
      };
    }
  }

  // Update Coin Selection mode and coin list for a trading strategy
  async updateStrategyCoinSelection(
    strategyId: number,
    values: {
      coinSelectionMode: 'TOP_N' | 'MANUAL' | 'ALL_COINS';
      coinLimit?: number;
      selectedCoins?: string[];
    },
    token?: string
  ): Promise<ApiResponse<any>> {
    try {
      const response = await axiosInstance.patch(
        `/api/admin/trading-strategies/${strategyId}/config/coin-selection`,
        values,
        {
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {})
          }
        }
      );
      return {
        success: true,
        data: response.data.data || response.data
      };
    } catch (error: any) {
      console.error('Error updating Coin Selection:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.response?.data?.error || error.message || 'Failed to update Coin Selection'
      };
    }
  }

  // ── AI auto-tune (Phase 2) — per-strategy ─────────────────────────────────
  async getAiAutoTuneGlobalSettings(): Promise<
    ApiResponse<{
      schedulerEnabled: boolean;
      schedulerIntervalMs: number;
      minIntervalMs: number;
      performanceSnapshotEnabled?: boolean;
      performanceTuneEnabled?: boolean;
      performanceMinTrades?: number;
      performanceTriggerMinIntervalMs?: number;
      performanceLlmEnabled?: boolean;
      performanceLlmProvider?: string;
      performanceLlmModel?: string;
      performanceLlmConfigured?: boolean;
    }>
  > {
    try {
      const response = await axiosInstance.get(
        '/api/admin/trading-strategies/ai-auto-tune/global-settings'
      );
      return { success: true, data: response.data.data || response.data };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to load AI auto-tune global settings',
      };
    }
  }

  async getAiPerformanceSnapshot(
    strategyId: number,
    window: string = '12'
  ): Promise<ApiResponse<AiPerformanceSnapshotPayload>> {
    try {
      const response = await axiosInstance.get(
        `/api/admin/trading-strategies/${strategyId}/ai-auto-tune/performance-snapshot`,
        { params: { window } }
      );
      return { success: true, data: response.data.data || response.data };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to load performance snapshot',
      };
    }
  }

  async updateAiAutoTuneSettings(
    strategyId: number,
    settings: {
      aiAutoTuneEnabled?: boolean;
      aiAutoTuneRequireApproval?: boolean;
      aiPerformanceTuneEnabled?: boolean;
      aiPerformanceTuneWindow?: number;
      aiPerformanceTuneMinTrades?: number;
      aiPerformanceCoinTuneEnabled?: boolean;
      aiPerformanceSideBlockEnabled?: boolean;
      aiPerformanceTuneLlmEnabled?: boolean;
      aiPerformanceTuneHistoryLearningEnabled?: boolean;
    }
  ): Promise<ApiResponse<any>> {
    try {
      const response = await axiosInstance.patch(
        `/api/admin/trading-strategies/${strategyId}/config/ai-auto-tune`,
        settings
      );
      return { success: true, data: response.data.data || response.data };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to update AI auto-tune settings'
      };
    }
  }

  async getAiAutoTunePending(strategyId: number): Promise<ApiResponse<Record<string, unknown> | null>> {
    try {
      const response = await axiosInstance.get(
        `/api/admin/trading-strategies/${strategyId}/ai-auto-tune/pending`
      );
      return { success: true, data: response.data.data ?? null };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to load pending AI suggestion'
      };
    }
  }

  async applyAiAutoTunePending(strategyId: number): Promise<ApiResponse<any>> {
    try {
      const response = await axiosInstance.post(
        `/api/admin/trading-strategies/${strategyId}/ai-auto-tune/apply-pending`
      );
      return { success: true, data: response.data.data || response.data };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to apply pending AI changes'
      };
    }
  }

  async dismissAiAutoTunePending(strategyId: number): Promise<ApiResponse<any>> {
    try {
      const response = await axiosInstance.post(
        `/api/admin/trading-strategies/${strategyId}/ai-auto-tune/dismiss-pending`
      );
      return { success: true, data: response.data.data || response.data };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to dismiss pending AI suggestion'
      };
    }
  }

  async runAiAutoTuneNow(strategyId: number): Promise<ApiResponse<{
    outcome: string;
    skipReason?: string;
    message?: string;
    marketRegime?: string;
    regimeLabel?: string;
    confidence?: number;
    fieldEvaluation?: Array<{
      field?: string;
      label?: string;
      current?: string | null;
      suggested?: string | null;
      wouldChange?: boolean;
    }>;
    proposedChangeCount?: number;
    changeCount?: number;
    pending?: Record<string, unknown> | null;
  }>> {
    try {
      const response = await axiosInstance.post(
        `/api/admin/trading-strategies/${strategyId}/ai-auto-tune/run-now`
      );
      return { success: true, data: response.data.data || response.data };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to run AI auto-tune'
      };
    }
  }

  async getAiAutoTuneHistory(
    strategyId: number,
    page: number = 0,
    size: number = 20
  ): Promise<ApiResponse<{
    content: Array<{
      id: number;
      strategyId: number;
      strategyName?: string;
      appliedAt: string;
      marketRegime?: string;
      trigger?: string;
      outcome?: string;
      actor?: string;
      changes?: Array<{ field?: string; label?: string; before?: string | null; after?: string | null }>;
      snapshotBefore?: Record<string, unknown>;
      snapshotAfter?: Record<string, unknown>;
    }>;
    page: number;
    size: number;
    totalElements: number;
    totalPages: number;
    last: boolean;
  }>> {
    try {
      const response = await axiosInstance.get(
        `/api/admin/trading-strategies/${strategyId}/ai-auto-tune/history?page=${page}&size=${size}`
      );
      return { success: true, data: response.data.data || response.data };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to load AI auto-tune history',
      };
    }
  }

  /** US-RDX-01: live regime gate status for all active strategies. */
  async getRegimeStatus(): Promise<ApiResponse<RegimeStatusResult>> {
    try {
      const response = await axiosInstance.get('/api/admin/trading-strategies/regime-status');
      return { success: true, data: response.data.data || response.data };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to load regime status',
      };
    }
  }

  async getRegimeReview(
    strategyId: number,
    from?: string,
    to?: string
  ): Promise<ApiResponse<StrategyRegimeReviewData>> {
    try {
      const params = new URLSearchParams();
      if (from) params.set('from', from);
      if (to) params.set('to', to);
      const qs = params.toString();
      const response = await axiosInstance.get(
        `/api/admin/trading-strategies/${strategyId}/regime-review${qs ? `?${qs}` : ''}`
      );
      return { success: true, data: response.data.data || response.data };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to load regime review',
      };
    }
  }

  // ── AI-suggest strategy config from live meme market (read-only) ─────────
  async suggestAiStrategyConfig(
    marketType: 'FUTURES' | 'SPOT' = 'FUTURES'
  ): Promise<ApiResponse<AiStrategySuggestResult>> {
    try {
      const response = await axiosInstance.get(
        '/api/admin/trading-strategies/ai-suggest-config',
        { params: { marketType } }
      );
      return {
        success: true,
        data: response.data.data || response.data
      };
    } catch (error: any) {
      console.error('Error fetching AI strategy suggestion:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to generate AI strategy suggestion'
      };
    }
  }

  async suggestCryptoAiStrategyConfig(
    marketType: 'FUTURES' | 'SPOT' = 'FUTURES',
    options?: {
      coinSelectionMode?: string;
      coinLimit?: number;
      selectedCoins?: string[];
    }
  ): Promise<ApiResponse<AiStrategySuggestResult>> {
    try {
      const params: Record<string, string | number> = { marketType };
      if (options?.coinSelectionMode) params.coinSelectionMode = options.coinSelectionMode;
      if (options?.coinLimit != null) params.coinLimit = options.coinLimit;
      if (options?.selectedCoins?.length) params.selectedCoins = options.selectedCoins.join(',');
      const response = await axiosInstance.get(
        '/api/admin/trading-strategies/ai-suggest-config/crypto',
        { params }
      );
      return {
        success: true,
        data: response.data.data || response.data
      };
    } catch (error: any) {
      console.error('Error fetching crypto AI strategy suggestion:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to generate crypto AI strategy suggestion'
      };
    }
  }

  async getMajorsUniverse(
    marketType: 'FUTURES' | 'SPOT' = 'FUTURES',
    limit = 0,
    includeStockIndex = false
  ): Promise<ApiResponse<string[]>> {
    try {
      const response = await axiosInstance.get(
        '/api/admin/trading-strategies/universe/majors',
        { params: { marketType, limit, includeStockIndex } }
      );
      return {
        success: true,
        data: response.data.data || []
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to load major crypto universe'
      };
    }
  }

  async getStockIndexUniverse(
    marketType: 'FUTURES' | 'SPOT' = 'FUTURES',
    limit = 0
  ): Promise<ApiResponse<string[]>> {
    try {
      const response = await axiosInstance.get(
        '/api/admin/trading-strategies/universe/stock-index',
        { params: { marketType, limit } }
      );
      return {
        success: true,
        data: response.data.data || []
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to load stock-index universe'
      };
    }
  }

  // ── Predict best meme coins for a strategy ───────────────────────────────
  async predictStrategyCoins(
    strategyId: number,
    marketType: 'FUTURES' | 'SPOT' = 'FUTURES'
  ): Promise<ApiResponse<CoinPrediction[]>> {
    try {
      const response = await axiosInstance.get(
        `/api/admin/trading-strategies/${strategyId}/predict-coins`,
        { params: { marketType } }
      );
      return {
        success: true,
        data: response.data.data || []
      };
    } catch (error: any) {
      console.error('Error predicting coins:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to predict coins'
      };
    }
  }

  // ── Clone a strategy ─────────────────────────────────────────────────────
  async cloneStrategy(
    strategyId: number,
    overrides: {
      name?: string;
      takeProfit?: number;
      basketTakeProfit?: number;
      fixedOrderAmount?: number;
      leverage?: number;
      maxOpenPositions?: number;
      coinSelectionMode?: 'TOP_N' | 'MANUAL';
      coinLimit?: number;
      selectedCoins?: string[];
    }
  ): Promise<ApiResponse<any>> {
    try {
      const response = await axiosInstance.post(
        `/api/admin/trading-strategies/${strategyId}/clone`,
        overrides
      );
      return { success: true, data: response.data.data || response.data };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Clone failed'
      };
    }
  }

  // Publish a strategy — makes it visible to retail users for enrollment
  async publishStrategy(strategyId: number): Promise<ApiResponse<any>> {
    try {
      const response = await axiosInstance.post(
        `/api/admin/trading-strategies/${strategyId}/publish`
      );
      return { success: true, data: response.data.data || response.data };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Publish failed'
      };
    }
  }

  // Unpublish a strategy — hides it from retail users (existing allocations unaffected)
  async unpublishStrategy(strategyId: number): Promise<ApiResponse<any>> {
    try {
      const response = await axiosInstance.post(
        `/api/admin/trading-strategies/${strategyId}/unpublish`
      );
      return { success: true, data: response.data.data || response.data };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Unpublish failed'
      };
    }
  }

  // Set (or clear) the required subscription plan for a strategy
  async setStrategyRequiredPlan(strategyId: number, requiredPlan: string | null): Promise<ApiResponse<any>> {
    try {
      const response = await axiosInstance.post(
        `/api/admin/trading-strategies/${strategyId}/set-required-plan`,
        { requiredPlan }
      );
      return { success: true, data: response.data.data || response.data };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to set required plan'
      };
    }
  }

  // Update Trading Account (kucoinConfigId) for a trading strategy
  async updateStrategyTradingAccount(
    strategyId: number,
    kucoinConfigId: number | null,
    token?: string
  ): Promise<ApiResponse<any>> {
    try {
      const response = await axiosInstance.patch(
        `/api/admin/trading-strategies/${strategyId}/config/trading-account`,
        { kucoinConfigId: kucoinConfigId },
        {
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {})
          }
        }
      );
      return {
        success: true,
        data: response.data.data || response.data
      };
    } catch (error: any) {
      console.error('Error updating Trading Account:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.response?.data?.error || error.message || 'Failed to update Trading Account'
      };
    }
  }

  // Get all trading strategies
  async getAllTradingStrategies(token?: string): Promise<ApiResponse<any[]>> {
    return this.makeRequest<any[]>('/api/admin/trading-strategies', {
      method: 'GET',
    }, token);
  }

  // Reconciliation
  async getStrategyReconciliationData(strategyId: number, token?: string): Promise<ApiResponse<any[]>> {
    return this.makeRequest<any[]>(`/api/admin/trading-strategies/${strategyId}/reconciliation`, {
      method: 'GET',
    }, token);
  }

  async reconcileStrategyWithKuCoin(strategyId: number, token?: string): Promise<ApiResponse<any>> {
    return this.makeRequest<any>(`/api/admin/trading-strategies/${strategyId}/reconciliation/check`, {
      method: 'POST',
    }, token);
  }

  async fixReconciliationDiscrepancies(discrepancies: any[], token?: string): Promise<ApiResponse<any>> {
    return this.makeRequest<any>('/api/admin/trading-strategies/reconciliation/fix', {
      method: 'POST',
      body: JSON.stringify(discrepancies),
    }, token);
  }

  async backfillMissingCloseOrderIds(strategyId: number, token?: string): Promise<ApiResponse<any>> {
    return this.makeRequest<any>(`/api/admin/trading-strategies/${strategyId}/reconciliation/backfill`, {
      method: 'POST',
    }, token);
  }

  async syncClosedTradesFromKuCoinHistory(
    strategyId: number,
    importMissing: boolean = false,
    token?: string
  ): Promise<ApiResponse<any>> {
    const qs = importMissing ? '?importMissing=true' : '';
    return this.makeRequest<any>(
      `/api/admin/trading-strategies/${strategyId}/reconciliation/sync-kucoin-history${qs}`,
      { method: 'POST' },
      token
    );
  }

  async fixHedgePositions(strategyId: number, token?: string): Promise<ApiResponse<any>> {
    return this.makeRequest<any>(`/api/admin/trading-strategies/${strategyId}/reconciliation/fix-hedge-positions`, {
      method: 'POST',
    }, token);
  }

  // Live Data
  async getLiveData(marketType: string = 'FUTURES', token?: string): Promise<ApiResponse<any[]>> {
    return this.makeRequest<any[]>(`/api/admin/trading-strategies/live-data?marketType=${marketType}`, {
      method: 'GET',
    }, token);
  }

   // Hedge-based Live Data (per-leg LONG/SHORT from hedge_positions)
   async getHedgeLiveData(marketType: string = 'FUTURES', token?: string): Promise<ApiResponse<any[]>> {
     return this.makeRequest<any[]>(`/api/admin/trading-strategies/hedge-live-data?marketType=${marketType}`, {
       method: 'GET',
     }, token);
   }

  // CSV Export Methods
  async exportTradingStrategyDetailedTransactionsCSV(token?: string): Promise<Blob> {
    const headers: Record<string, string> = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    const response = await axiosInstance.get('/api/admin/trading-strategies/reports/transactions/detailed/export/csv', {
      headers,
      responseType: 'blob'
    });
    return response.data;
  }

  async exportTradingStrategySummaryCSV(token?: string): Promise<Blob> {
    const headers: Record<string, string> = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    const response = await axiosInstance.get('/api/admin/trading-strategies/reports/summary/export/csv', {
      headers,
      responseType: 'blob'
    });
    return response.data;
  }

  async exportTradingStrategyPnlSummaryCSV(token?: string): Promise<Blob> {
    const headers: Record<string, string> = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    const response = await axiosInstance.get('/api/admin/trading-strategies/reports/pnl-summary/export/csv', {
      headers,
      responseType: 'blob'
    });
    return response.data;
  }

  async exportTradingStrategyComparisonCSV(token?: string): Promise<Blob> {
    const headers: Record<string, string> = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    const response = await axiosInstance.get('/api/admin/trading-strategies/reports/trading-type-comparison/export/csv', {
      headers,
      responseType: 'blob'
    });
    return response.data;
  }

  // PDF Export Methods
  async exportTradingStrategyDetailedTransactionsPDF(token?: string): Promise<Blob> {
    const headers: Record<string, string> = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    const response = await axiosInstance.get('/api/admin/trading-strategies/reports/transactions/detailed/export/pdf', {
      headers,
      responseType: 'blob'
    });
    return response.data;
  }

  async exportTradingStrategySummaryPDF(token?: string): Promise<Blob> {
    const headers: Record<string, string> = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    const response = await axiosInstance.get('/api/admin/trading-strategies/reports/summary/export/pdf', {
      headers,
      responseType: 'blob'
    });
    return response.data;
  }

  async exportTradingStrategyPnlSummaryPDF(token?: string): Promise<Blob> {
    const headers: Record<string, string> = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    const response = await axiosInstance.get('/api/admin/trading-strategies/reports/pnl-summary/export/pdf', {
      headers,
      responseType: 'blob'
    });
    return response.data;
  }

  async exportTradingStrategyComparisonPDF(token?: string): Promise<Blob> {
    const headers: Record<string, string> = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    const response = await axiosInstance.get('/api/admin/trading-strategies/reports/trading-type-comparison/export/pdf', {
      headers,
      responseType: 'blob'
    });
    return response.data;
  }

  async exportTradingStrategyDetailedTransactionsWithUsersCSV(token?: string): Promise<Blob> {
    const headers: Record<string, string> = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    const response = await axiosInstance.get('/api/admin/trading-strategies/reports/transactions/detailed-with-users/export/csv', {
      headers,
      responseType: 'blob'
    });
    return response.data;
  }

  async exportTradingStrategyDetailedTransactionsWithUsersPDF(token?: string): Promise<Blob> {
    const headers: Record<string, string> = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    const response = await axiosInstance.get('/api/admin/trading-strategies/reports/transactions/detailed-with-users/export/pdf', {
      headers,
      responseType: 'blob'
    });
    return response.data;
  }

  async exportTradingStrategyRecentTransactionsCSV(days: number = 30, token?: string): Promise<Blob> {
    const headers: Record<string, string> = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    const response = await axiosInstance.get(`/api/admin/trading-strategies/reports/transactions/recent/export/csv?days=${days}`, {
      headers,
      responseType: 'blob'
    });
    return response.data;
  }

  async exportTradingStrategyRecentTransactionsPDF(days: number = 30, token?: string): Promise<Blob> {
    const headers: Record<string, string> = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    const response = await axiosInstance.get(`/api/admin/trading-strategies/reports/transactions/recent/export/pdf?days=${days}`, {
      headers,
      responseType: 'blob'
    });
    return response.data;
  }

  // Get strategy logs (tail -f -n 500 | grep "StrategyName")
  // US13: Enhanced to support filtering by symbol, side, and positionId for leg-specific logs
  async getStrategyLogs(
    strategyName: string, 
    lines?: number,
    tokenOrFilters?: string | { symbol?: string; side?: 'LONG' | 'SHORT'; positionId?: string }
  ): Promise<ApiResponse<{ logs: string[] }>> {
    // Handle different call patterns
    const actualLines = lines ?? 500;
    let actualToken: string | undefined;
    let actualFilters: { symbol?: string; side?: 'LONG' | 'SHORT'; positionId?: string } | undefined;
    
    if (typeof tokenOrFilters === 'string') {
      // Old signature: (strategyName, lines?, token)
      actualToken = tokenOrFilters;
    } else if (tokenOrFilters) {
      // New signature: (strategyName, lines?, filters)
      actualFilters = tokenOrFilters;
    }
    const effectiveLines = actualLines;
    let url = `/api/admin/trading-strategies/logs?strategyName=${encodeURIComponent(strategyName)}&lines=${effectiveLines}`;
    
    // US13: Add optional filter parameters
    if (actualFilters) {
      if (actualFilters.symbol) {
        url += `&symbol=${encodeURIComponent(actualFilters.symbol)}`;
      }
      if (actualFilters.side) {
        url += `&side=${encodeURIComponent(actualFilters.side)}`;
      }
      if (actualFilters.positionId) {
        url += `&positionId=${encodeURIComponent(actualFilters.positionId)}`;
      }
    }
    
    return this.makeRequest<{ logs: string[] }>(url, {}, actualToken);
  }

  // US6 / US16: Close a single hedge leg by its hedgePositionId
  async closeHedgeLeg(legId: number, token?: string, reason?: string): Promise<ApiResponse<{ legId: number; reason?: string }>> {
    let url = `/api/admin/trading-strategies/hedge-legs/${legId}/close`;
    if (reason && reason.trim().length > 0) {
      url += `?reason=${encodeURIComponent(reason.trim())}`;
    }
    return this.makeRequest<{ legId: number; reason?: string }>(
      url,
      {
        method: 'POST'
      },
      token
    );
  }

  async closePosition(positionId: string, symbol: string, positionType: 'LONG' | 'SHORT', token?: string, reason?: string): Promise<ApiResponse<{ positionId: string; symbol: string; positionType: string; closeOrderId?: string; reason?: string }>> {
    let url = `/api/admin/trading-strategies/positions/${positionId}/close?symbol=${encodeURIComponent(symbol)}&positionType=${positionType}`;
    if (reason && reason.trim().length > 0) {
      url += `&reason=${encodeURIComponent(reason.trim())}`;
    }
    return this.makeRequest<{ positionId: string; symbol: string; positionType: string; closeOrderId?: string; reason?: string }>(
      url,
      {
        method: 'POST'
      },
      token
    );
  }

  // System Settings
  async getSystemSettings(token?: string): Promise<ApiResponse<any>> {
    return this.makeRequest<any>('/admin/settings', {}, token);
  }

  async updateSystemSettings(settings: any, token?: string): Promise<ApiResponse<void>> {
    return this.makeRequest<void>('/admin/settings', {
      method: 'PUT',
      body: JSON.stringify(settings)
    }, token);
  }

  async createCurrencyAccount(customerId: number, accountType: string, currency: string, token?: string): Promise<ApiResponse<any>> {
    return this.makeRequest<any>(
      `/admin/customers/${customerId}/accounts`,
      {
        method: 'POST',
        body: JSON.stringify({ accountType, currency })
      },
      token
    );
  }

  // Provider Config Management Methods
  async getProviderConfigs(page: number = 0, size: number = 10, token?: string): Promise<ApiResponse<any> & {
    totalElements?: number;
    totalPages?: number;
    currentPage?: number;
    pageSize?: number;
  }> {
    return this.makeRequest(`/api/admin/providers?page=${page}&size=${size}`, {
      method: 'GET'
    }, token);
  }

  async getProviderConfigsByProvider(provider: string, page: number = 0, size: number = 10, token?: string): Promise<ApiResponse<any> & {
    totalElements?: number;
    totalPages?: number;
    currentPage?: number;
    pageSize?: number;
  }> {
    return this.makeRequest(`/api/admin/providers/${provider}?page=${page}&size=${size}`, {
      method: 'GET'
    }, token);
  }

  async updateProviderConfig(provider: string, key: string, value: string, token?: string): Promise<ApiResponse<any>> {
    return this.makeRequest(`/api/admin/providers/${provider}/${key}`, {
      method: 'PUT',
      body: JSON.stringify({ value: value })
    }, token);
  }

  // Trading Provider Methods
  async getTradingProviderStatus(token?: string): Promise<ApiResponse<any>> {
    return this.makeRequest<any>('/api/admin/trading/providers/status', {
      method: 'GET'
    }, token);
  }

  async getCurrentTradingProvider(token?: string): Promise<ApiResponse<any>> {
    return this.makeRequest<any>('/api/admin/trading/provider', {
      method: 'GET'
    }, token);
  }

  async setTradingProvider(provider: string, validateOnly: boolean = false, token?: string): Promise<ApiResponse<any>> {
    return this.makeRequest<any>('/api/admin/trading/provider', {
      method: 'PUT',
      body: JSON.stringify({ provider, validateOnly }),
      headers: {
        'Content-Type': 'application/json'
      }
    }, token);
  }

  async testTradingProviderConnection(provider: string, token?: string): Promise<ApiResponse<any>> {
    return this.makeRequest<any>(`/api/admin/trading/providers/${provider}/test`, {
      method: 'POST'
    }, token);
  }

  // Asset Token methods
  async getAssetTokens(token?: string): Promise<ApiResponse<any[]>> {
    return this.makeRequest<any[]>('/admin/asset-tokens', {}, token);
  }

  async createAssetToken(tokenData: any, token?: string): Promise<ApiResponse<any>> {
    return this.makeRequest<any>('/admin/asset-tokens', {
      method: 'POST',
      body: JSON.stringify(tokenData)
    }, token);
  }

  async updateAssetTokenValue(tokenId: number, newValue: number, token?: string): Promise<ApiResponse<any>> {
    return this.makeRequest<any>(`/asset-tokens/${tokenId}/value`, {
      method: 'PUT',
      body: JSON.stringify({ value: newValue })
    }, token);
  }

  async updateAssetTokenStatus(tokenId: number, status: string, token?: string): Promise<ApiResponse<any>> {
    return this.makeRequest<any>(`/asset-tokens/${tokenId}/status`, {
      method: 'PUT',
      body: JSON.stringify({ status })
    }, token);
  }

  async getSessionSettings(token?: string): Promise<ApiResponse<any>> {
    return this.makeRequest<any>('/admin/settings/session', {
      method: 'GET'
    }, token);
  }

  async updateSessionSettings(settings: any, token?: string): Promise<ApiResponse<any>> {
    return this.makeRequest<any>('/admin/settings/session', {
      method: 'PUT',
      body: JSON.stringify(settings)
    }, token);
  }

  async getInactivitySettings(token?: string): Promise<ApiResponse<any>> {
    return this.makeRequest<any>('/admin/settings/inactivity', {
      method: 'GET'
    }, token);
  }

  async updateInactivitySettings(settings: any, token?: string): Promise<ApiResponse<any>> {
    return this.makeRequest<any>('/admin/settings/inactivity', {
      method: 'PUT',
      body: JSON.stringify(settings)
    }, token);
  }

  async getSecuritySettings(token?: string): Promise<ApiResponse<any>> {
    return this.makeRequest<any>('/admin/settings/security', {
      method: 'GET'
    }, token);
  }

  async updateSecuritySettings(settings: any, token?: string): Promise<ApiResponse<any>> {
    return this.makeRequest<any>('/admin/settings/security', {
      method: 'PUT',
      body: JSON.stringify(settings)
    }, token);
  }

  async getLoginDisplaySettings(token?: string): Promise<ApiResponse<any>> {
    return this.makeRequest<any>('/api/admin/login-display-settings', {
      method: 'GET'
    }, token);
  }

  async updateLoginDisplaySettings(settings: any, token?: string): Promise<ApiResponse<any>> {
    return this.makeRequest<any>('/api/admin/login-display-settings', {
      method: 'PUT',
      body: JSON.stringify(settings)
    }, token);
  }

  async get<T = any>(endpoint: string, token?: string): Promise<ApiResponse<T>> {
    return this.makeRequest<T>(endpoint, {}, token);
  }

  async post<T = any>(endpoint: string, data: any, token?: string): Promise<ApiResponse<T>> {
    return this.makeRequest<T>(endpoint, {
      method: 'POST',
      body: JSON.stringify(data)
    }, token);
  }

  async getKycSubmissions(page: number = 0, size: number = 100, status?: string, productId?: number, token?: string): Promise<ApiResponse<{ content: KycSubmission[]; totalElements: number; totalPages: number }>> {
    const params = new URLSearchParams();
    params.append('page', page.toString());
    params.append('size', size.toString());
    if (status) {
      params.append('status', status);
    }
    if (productId) {
      params.append('productId', productId.toString());
    }
    return this.makeRequest<{ content: KycSubmission[]; totalElements: number; totalPages: number }>(
      `/admin/kyc-submissions?${params.toString()}`,
      {},
      token
    );
  }

  async getKycSubmissionDetails(id: number, token?: string): Promise<ApiResponse<any>> {
    return this.makeRequest(`/admin/kyc-submissions/${id}`, {}, token);
  }

  async getKybSubmissionDetails(id: number, token?: string): Promise<ApiResponse<any>> {
    return this.makeRequest(`/admin/kyb-submissions/${id}`, {}, token);
  }

  async listKycDocuments(id: number, token?: string): Promise<ApiResponse<any[]>> {
    return this.makeRequest(`/admin/kyc-submissions/${id}/documents`, {}, token);
  }

  async uploadKycDocuments(id: number, files: File[], types?: string[], notes?: string[], token?: string): Promise<ApiResponse<any[]>> {
    const form = new FormData();
    files.forEach(f => form.append('files', f));
    if (files[0]) {
      // Add a single-file field for backends expecting 'file'
      form.append('file', files[0]);
    }
    if (types && types.length) types.forEach(t => form.append('types', t));
    if (notes && notes.length) notes.forEach(n => form.append('notes', n));
    try {
      const response = await axiosInstance.post(`/admin/kyc-submissions/${id}/documents`, form, {
        withCredentials: true
      });
      return response.data as ApiResponse<any[]>;
    } catch (error: any) {
      console.error('uploadKycDocuments error:', error);
      return { success: false, error: error?.response?.data?.message || error.message || 'Upload failed' } as any;
    }
  }

  // Review KYC submission
  async reviewKycSubmission(submissionId: number, status?: string, comments?: string, riskLevel?: string, token?: string): Promise<ApiResponse<any>> {
    const params = new URLSearchParams();
    if (status) params.append('status', status);
    if (comments) params.append('comments', comments);
    if (riskLevel) params.append('riskLevel', riskLevel);
    
    return this.makeRequest(`/admin/kyc-submissions/${submissionId}/review?${params.toString()}`, {
      method: 'POST'
    }, token);
  }

  async getKybSubmissions(page: number = 0, size: number = 100, status?: string, productId?: number, token?: string): Promise<ApiResponse<{ content: KybSubmission[]; totalElements: number; totalPages: number }>> {
    const params = new URLSearchParams();
    params.append('page', page.toString());
    params.append('size', size.toString());
    if (status) params.append('status', status);
    if (productId) params.append('productId', productId.toString());
    
    return this.makeRequest(`/admin/kyb-submissions?${params.toString()}`, {}, token);
  }

  async listKybDocuments(id: number, token?: string): Promise<ApiResponse<any[]>> {
    return this.makeRequest(`/admin/kyb-submissions/${id}/documents`, {}, token);
  }

  async uploadKybDocuments(id: number, files: File[], types?: string[], notes?: string[], token?: string): Promise<ApiResponse<any[]>> {
    const form = new FormData();
    files.forEach(f => form.append('files', f));
    if (files[0]) {
      form.append('file', files[0]);
    }
    if (types && types.length) types.forEach(t => form.append('types', t));
    if (notes && notes.length) notes.forEach(n => form.append('notes', n));
    try {
      const response = await axiosInstance.post(`/admin/kyb-submissions/${id}/documents`, form, {
        withCredentials: true
      });
      return response.data as ApiResponse<any[]>;
    } catch (error: any) {
      console.error('uploadKybDocuments error:', error);
      return { success: false, error: error?.response?.data?.message || error.message || 'Upload failed' } as any;
    }
  }

  // Review KYB submission
  async reviewKybSubmission(submissionId: number, status?: string, comments?: string, riskLevel?: string, token?: string): Promise<ApiResponse<any>> {
    const params = new URLSearchParams();
    if (status) params.append('status', status);
    if (comments) params.append('comments', comments);
    if (riskLevel) params.append('riskLevel', riskLevel);
    
    return this.makeRequest(`/admin/kyb-submissions/${submissionId}/review?${params.toString()}`, {
      method: 'POST'
    }, token);
  }

  // Asset Token Investment tracking methods
  async getAssetTokenInvestments(assetTokenId: number, token?: string): Promise<ApiResponse<AssetTokenInvestment[]>> {
    return this.makeRequest(`/admin/asset-token-investments/token/${assetTokenId}`);
  }

  async getUserAssetTokenInvestments(userId: number, token?: string): Promise<ApiResponse<AssetTokenInvestment[]>> {
    return this.makeRequest(`/admin/asset-token-investments/user/${userId}`);
  }

  async getAssetTokenInvestorCount(assetTokenId: number, token?: string): Promise<ApiResponse<number>> {
    return this.makeRequest(`/admin/asset-token-investments/token/${assetTokenId}/investor-count`);
  }

  async getAssetTokenTotalInvestment(assetTokenId: number, token?: string): Promise<ApiResponse<number>> {
    return this.makeRequest(`/admin/asset-token-investments/token/${assetTokenId}/total-investment`);
  }

  async getAssetTokenInvestmentStatistics(token?: string): Promise<ApiResponse<any>> {
    return this.makeRequest(`/admin/asset-token-investments/statistics`);
  }

  async createAssetTokenInvestment(investmentData: any, token?: string): Promise<ApiResponse<AssetTokenInvestment>> {
    return this.makeRequest(`/admin/asset-token-investments/create`, {
      method: 'POST',
      body: JSON.stringify(investmentData)
    });
  }

  async getTransactionCounts(): Promise<ApiResponse<TransactionCounts>> {
    try {
      const response = await this.makeRequest<TransactionCounts>('/admin/transactions/counts', {
        method: 'GET'
      });
      return response;
    } catch (error) {
      return {
        success: false,
        error: 'Failed to fetch transaction counts',
        data: null
      };
    }
  }

  // KYC/KYB Summary methods
  async getKycSummary(): Promise<ApiResponse<KycKybSummary>> {
    try {
      const response = await this.makeRequest<KycKybSummary>('/admin/dashboard/kyc/summary', {
        method: 'GET'
      });
      return response;
    } catch (error) {
      return {
        success: false,
        error: 'Failed to fetch KYC summary',
        data: null
      };
    }
  }

  async getKybSummary(): Promise<ApiResponse<KycKybSummary>> {
    try {
      const response = await this.makeRequest<KycKybSummary>('/admin/dashboard/kyb/summary', {
        method: 'GET'
      });
      return response;
    } catch (error) {
      return {
        success: false,
        error: 'Failed to fetch KYB summary',
        data: null
      };
    }
  }

  // Trading Bot Admin APIs
  async getTradingBots(token?: string): Promise<ApiResponse<any[]>> {
    return this.makeRequest<any[]>(`/trading/bots`, {}, token);
  }

  async createTradingBot(bot: {
    name: string; symbol: string; timeframe: string; strategy: string; maxNotionalUsd: number; defaultMode?: 'PAPER'|'LIVE'|'SHADOW';
    primaryStrategy?: string; secondaryStrategy?: string;
  }, token?: string): Promise<ApiResponse<any>> {
    return this.makeRequest<any>(`/trading/bots`, { method: 'POST', body: JSON.stringify(bot) }, token);
  }

  async startTradingBot(botId: number, mode?: 'PAPER'|'LIVE'|'SHADOW', token?: string): Promise<ApiResponse<any>> {
    return this.makeRequest<any>(`/trading/bots/${botId}/start`, { method: 'POST', body: JSON.stringify({ mode }) }, token);
  }

  async stopTradingBot(botId: number, token?: string): Promise<ApiResponse<any>> {
    return this.makeRequest<any>(`/trading/bots/${botId}/stop`, { method: 'POST' }, token);
  }

  async getTradingBotStatus(botId: number, token?: string): Promise<ApiResponse<any>> {
    return this.makeRequest<any>(`/trading/bots/${botId}/status`, {}, token);
  }

  async deleteTradingBot(botId: number, token?: string): Promise<ApiResponse<void>> {
    return this.makeRequest<void>(`/trading/bots/${botId}` , { method: 'DELETE' }, token);
  }

  getTradingBotStatusStreamUrl(botId: number): string {
    return `${this.baseUrl}/trading/bots/${botId}/status/stream`;
  }

  // Admin Personal KuCoin Trading Accounts (My Accounts)
  async getMyTradingAccounts(token?: string): Promise<ApiResponse<any[]>> {
    return this.makeRequest<any[]>(`/api/admin/trading/my-accounts`, {
      method: 'GET'
    }, token);
  }

  /**
   * Returns all KuCoin accounts the current admin can assign to strategies:
   *   - Shared / system accounts (adminUser IS NULL)
   *   - The admin's own personal accounts
   * Each entry: { id, name, shared }
   */
  async getAllKucoinAccounts(token?: string): Promise<ApiResponse<any[]>> {
    return this.makeRequest<any[]>(`/api/admin/trading/my-accounts/all-kucoin-accounts`, {
      method: 'GET'
    }, token);
  }

  async getMyTradingAccountById(id: number, token?: string): Promise<ApiResponse<any>> {
    return this.makeRequest<any>(`/api/admin/trading/my-accounts/${id}`, {
      method: 'GET'
    }, token);
  }

  async createMyTradingAccount(
    payload: {
      name: string;
      apiKey: string;
      apiSecret: string;
      apiPassphrase: string;
      baseUrl?: string;
      isActive?: boolean;
      kucoinCopyTradeLeadEnabled?: boolean;
    },
    token?: string
  ): Promise<ApiResponse<any>> {
    return this.makeRequest<any>(`/api/admin/trading/my-accounts`, {
      method: 'POST',
      body: JSON.stringify(payload)
    }, token);
  }

  async updateMyTradingAccount(
    id: number,
    payload: {
      name?: string;
      apiKey?: string;
      apiSecret?: string;
      apiPassphrase?: string;
      baseUrl?: string;
      isActive?: boolean;
      kucoinCopyTradeLeadEnabled?: boolean;
    },
    token?: string
  ): Promise<ApiResponse<any>> {
    return this.makeRequest<any>(`/api/admin/trading/my-accounts/${id}`, {
      method: 'PUT',
      body: JSON.stringify(payload)
    }, token);
  }

  async deleteMyTradingAccount(id: number, token?: string): Promise<ApiResponse<void>> {
    return this.makeRequest<void>(`/api/admin/trading/my-accounts/${id}`, {
      method: 'DELETE'
    }, token);
  }

  async testMyTradingAccount(id: number, token?: string): Promise<ApiResponse<any>> {
    return this.makeRequest<any>(`/api/admin/trading/my-accounts/${id}/test`, {
      method: 'POST'
    }, token);
  }

  // KuCoin sub-account provisioning (master → funded sub → strategy)
  async estimateStrategyCapital(
    payload: {
      fixedOrderAmount?: number;
      leverage?: number;
      maxOpenPositions?: number;
      marketType?: string;
    },
    token?: string
  ): Promise<ApiResponse<any>> {
    return this.makeRequest<any>(`/api/admin/trading/kucoin-subaccounts/estimate-capital`, {
      method: 'POST',
      body: JSON.stringify(payload)
    }, token);
  }

  async getKucoinMasterDepositInfo(token?: string): Promise<ApiResponse<any>> {
    return this.makeRequest<any>(`/api/admin/trading/kucoin-subaccounts/master-deposit-info`, {
      method: 'GET'
    }, token);
  }

  async createKucoinSubaccountRequest(
    payload: {
      strategyId?: number;
      fixedOrderAmount?: number;
      leverage?: number;
      maxOpenPositions?: number;
      marketType?: string;
      strategyConfigSnapshot?: string;
    },
    token?: string
  ): Promise<ApiResponse<any>> {
    return this.makeRequest<any>(`/api/admin/trading/kucoin-subaccounts/requests`, {
      method: 'POST',
      body: JSON.stringify(payload)
    }, token);
  }

  async getMyKucoinSubaccountRequests(token?: string): Promise<ApiResponse<any[]>> {
    return this.makeRequest<any[]>(`/api/admin/trading/kucoin-subaccounts/requests`, {
      method: 'GET'
    }, token);
  }

  async reportKucoinSubaccountDeposit(
    requestId: number,
    payload: { amount: number; txReference?: string },
    token?: string
  ): Promise<ApiResponse<any>> {
    return this.makeRequest<any>(
      `/api/admin/trading/kucoin-subaccounts/requests/${requestId}/report-deposit`,
      { method: 'POST', body: JSON.stringify(payload) },
      token
    );
  }

  async cancelKucoinSubaccountRequest(requestId: number, token?: string): Promise<ApiResponse<any>> {
    return this.makeRequest<any>(
      `/api/admin/trading/kucoin-subaccounts/requests/${requestId}/cancel`,
      { method: 'POST' },
      token
    );
  }

  async getKucoinSubaccountMyWallets(blockchain?: string, token?: string): Promise<ApiResponse<any[]>> {
    const q = blockchain ? `?blockchain=${encodeURIComponent(blockchain)}` : '';
    return this.makeRequest<any[]>(`/api/admin/trading/kucoin-subaccounts/my-wallets${q}`, {
      method: 'GET'
    }, token);
  }

  async createKucoinSubaccountWallet(blockchain: string, token?: string): Promise<ApiResponse<any>> {
    return this.makeRequest<any>(`/api/admin/trading/kucoin-subaccounts/my-wallets/create`, {
      method: 'POST',
      body: JSON.stringify({ blockchain })
    }, token);
  }

  async payKucoinSubaccountWithWallet(
    requestId: number,
    payload: { walletId?: number; amount?: number },
    token?: string
  ): Promise<ApiResponse<any>> {
    return this.makeRequest<any>(
      `/api/admin/trading/kucoin-subaccounts/requests/${requestId}/pay-with-wallet`,
      { method: 'POST', body: JSON.stringify(payload) },
      token
    );
  }

  async getPendingKucoinSubaccountRequests(token?: string): Promise<ApiResponse<any[]>> {
    return this.makeRequest<any[]>(`/api/admin/trading/kucoin-subaccounts/requests/pending`, {
      method: 'GET'
    }, token);
  }

  async confirmKucoinSubaccountDeposit(
    requestId: number,
    payload: { confirmedAmount?: number; notes?: string },
    token?: string
  ): Promise<ApiResponse<any>> {
    return this.makeRequest<any>(
      `/api/admin/trading/kucoin-subaccounts/requests/${requestId}/confirm-deposit`,
      { method: 'POST', body: JSON.stringify(payload) },
      token
    );
  }

  async provisionKucoinSubaccount(
    requestId: number,
    payload?: {
      apiKey?: string;
      apiSecret?: string;
      apiPassphrase?: string;
      baseUrl?: string;
      useAutoApi?: boolean;
    },
    token?: string
  ): Promise<ApiResponse<any>> {
    return this.makeRequest<any>(
      `/api/admin/trading/kucoin-subaccounts/requests/${requestId}/provision`,
      { method: 'POST', body: JSON.stringify(payload || {}) },
      token
    );
  }

  // Multi-signature wallet methods
  async getMultiSignatureWallets(walletType?: string, token?: string): Promise<ApiResponse<any[]>> {
    const params = walletType ? `?walletType=${walletType}` : '';
    return this.makeRequest<any[]>(`/admin/multi-signature-wallets${params}`, {
      method: 'GET'
    }, token);
  }

  async getMultiSignatureWalletById(walletId: number, token?: string): Promise<ApiResponse<any>> {
    return this.makeRequest<any>(`/admin/multi-signature-wallets/${walletId}`, {
      method: 'GET'
    }, token);
  }

  async createMultiSignatureWallet(walletData: any, token?: string): Promise<ApiResponse<any>> {
    return this.makeRequest<any>('/admin/multi-signature-wallets', {
      method: 'POST',
      body: JSON.stringify(walletData)
    }, token);
  }

  async createPropertyEscrowWallet(walletData: any, token?: string): Promise<ApiResponse<any>> {
    return this.makeRequest<any>('/admin/multi-signature-wallets/property-escrow', {
      method: 'POST',
      body: JSON.stringify(walletData)
    }, token);
  }

  async activateMultiSignatureWallet(walletId: number, token?: string): Promise<ApiResponse<any>> {
    return this.makeRequest<any>(`/admin/multi-signature-wallets/${walletId}/activate`, {
      method: 'POST'
    }, token);
  }

  async freezeMultiSignatureWallet(walletId: number, token?: string): Promise<ApiResponse<any>> {
    return this.makeRequest<any>(`/admin/multi-signature-wallets/${walletId}/freeze`, {
      method: 'POST'
    }, token);
  }

  async getWalletSignatories(walletId: number, token?: string): Promise<ApiResponse<any[]>> {
    return this.makeRequest<any[]>(`/admin/multi-signature-wallets/${walletId}/signatories`, {
      method: 'GET'
    }, token);
  }

  async getWalletTransactions(walletId: number, token?: string): Promise<ApiResponse<any[]>> {
    return this.makeRequest<any[]>(`/admin/multi-signature-wallets/${walletId}/transactions`, {
      method: 'GET'
    }, token);
  }

  async createWalletTransaction(walletId: number, transactionData: any, token?: string): Promise<ApiResponse<any>> {
    return this.makeRequest<any>(`/admin/multi-signature-wallets/${walletId}/transactions`, {
      method: 'POST',
      body: JSON.stringify(transactionData)
    }, token);
  }

  async getAvailableUsers(token?: string): Promise<ApiResponse<User[]>> {
    return this.makeRequest<User[]>('/admin/multi-signature-wallets/available-users', {
      method: 'GET'
    }, token);
  }

  // Risk Management Methods
  async getGlobalRiskConfig(token?: string): Promise<ApiResponse<any>> {
    return this.makeRequest<any>('/admin/risk/config', {
      method: 'GET'
    }, token);
  }

  async updateGlobalRiskConfig(config: any, token?: string): Promise<ApiResponse<any>> {
    return this.makeRequest<any>('/admin/risk/config', {
      method: 'PUT',
      body: JSON.stringify(config)
    }, token);
  }

  async getRecentRiskViolations(hours: number = 24, token?: string): Promise<ApiResponse<any[]>> {
    return this.makeRequest<any[]>(`/admin/risk/violations?hours=${hours}`, {
      method: 'GET'
    }, token);
  }

  async getUnresolvedRiskViolations(token?: string): Promise<ApiResponse<any[]>> {
    return this.makeRequest<any[]>('/admin/risk/violations/unresolved', {
      method: 'GET'
    }, token);
  }

  async emergencyStopAll(token?: string): Promise<ApiResponse<any>> {
    return this.makeRequest<any>('/admin/risk/emergency-stop', {
      method: 'POST'
    }, token);
  }

  async resumeTrading(token?: string): Promise<ApiResponse<any>> {
    return this.makeRequest<any>('/admin/risk/resume', {
      method: 'POST'
    }, token);
  }

  async checkBotRisk(botId: number, token?: string): Promise<ApiResponse<any>> {
    return this.makeRequest<any>(`/admin/risk/check/${botId}`, {
      method: 'GET'
    }, token);
  }

  // Crypto Wallet Trading Methods
  async getCryptoWallets(token?: string): Promise<ApiResponse<any[]>> {
    return this.makeRequest<any[]>('/admin/crypto-trading/wallets', {
      method: 'GET'
    }, token);
  }

  async getUserCryptoWallets(userId: number, token?: string): Promise<ApiResponse<any[]>> {
    return this.makeRequest<any[]>(`/admin/crypto-trading/wallets/user/${userId}`, {
      method: 'GET'
    }, token);
  }

  async getBotCryptoAllocations(botId: number, token?: string): Promise<ApiResponse<any[]>> {
    return this.makeRequest<any[]>(`/admin/crypto-trading/bots/${botId}/allocations`, {
      method: 'GET'
    }, token);
  }

  async allocateCryptoToBot(allocationData: {
    userId: number;
    botId: number;
    cryptoWalletId: number;
    cryptoAmount: number;
    notes?: string;
  }, token?: string): Promise<ApiResponse<any>> {
    return this.makeRequest<any>('/admin/crypto-trading/allocate', {
      method: 'POST',
      body: JSON.stringify(allocationData)
    }, token);
  }

  async getUserCryptoTradingSummary(userId: number, token?: string): Promise<ApiResponse<any>> {
    return this.makeRequest<any>(`/admin/crypto-trading/summary/user/${userId}`, {
      method: 'GET'
    }, token);
  }

  async cancelCryptoAllocation(allocationId: number, userId: number, token?: string): Promise<ApiResponse<any>> {
    return this.makeRequest<any>(`/admin/crypto-trading/allocations/${allocationId}/cancel?userId=${userId}`, {
      method: 'POST'
    }, token);
  }

  async getAllCryptoAllocations(token?: string): Promise<ApiResponse<any[]>> {
    return this.makeRequest<any[]>('/admin/crypto-trading/allocations', {
      method: 'GET'
    }, token);
  }

  // Product Assignment Methods
  async assignProductsToAdminUser(adminUserId: number, productIds: number[], token?: string): Promise<ApiResponse<string>> {
    return this.makeRequest<string>(`/admin/profile/assign-products/${adminUserId}`, {
      method: 'POST',
      body: JSON.stringify(productIds),
      headers: {
        'Content-Type': 'application/json'
      }
    }, token);
  }

  async getAvailableProducts(token?: string): Promise<ApiResponse<any[]>> {
    return this.makeRequest<any[]>('/admin/profile/available-products', {
      method: 'GET'
    }, token);
  }

  // Product Management Methods
  async getProducts(page: number = 0, size: number = 50, status?: string, q?: string, token?: string): Promise<ApiResponse<any>> {
    const params = new URLSearchParams({
      page: page.toString(),
      size: size.toString(),
      ...(status && { status }),
      ...(q && { q })
    });
    return this.makeRequest<any>(`/admin/products?${params}`, {
      method: 'GET'
    }, token);
  }

  async createProduct(product: {
    name: string;
    type: string;
    customerType: 'PERSONAL' | 'BUSINESS';
    description?: string;
    isAvailable?: boolean;
    registrationFee?: number;
    registrationFeeType?: 'NO_FEE' | 'FIXED_ONE_OFF' | 'RECURRING_MONTHLY';
    requiresVerification?: boolean;
    syncRetailSubscriptionPlan?: boolean;
    registrationPaymentMethods?: 'ALL' | 'STRIPE' | 'CRYPTO';
    retailPermissionTemplate?: 'DEFAULT_RETAIL' | 'MARKET_SIGNALS_BASIC' | 'NONE';
    adminPermissionTemplate?: 'NONE' | 'OPERATIONS_MANAGER' | 'COMPLIANCE_OFFICER' | 'CUSTOMER_SUPPORT' | 'FINANCIAL_ANALYST' | 'RISK_MANAGER' | 'AUDITOR';
    retailPermissionAdds?: string[];
    retailPermissionRemoves?: string[];
    adminPermissionAdds?: string[];
    adminPermissionRemoves?: string[];
  }, token?: string): Promise<ApiResponse<any>> {
    return this.makeRequest<any>('/admin/products', {
      method: 'POST',
      body: JSON.stringify(product),
      headers: {
        'Content-Type': 'application/json'
      }
    }, token);
  }

  async updateProduct(productId: number, product: {
    name?: string;
    type?: string;
    customerType?: 'PERSONAL' | 'BUSINESS';
    description?: string;
    isAvailable?: boolean;
    registrationFee?: number;
    registrationFeeType?: 'NO_FEE' | 'FIXED_ONE_OFF' | 'RECURRING_MONTHLY';
    requiresVerification?: boolean;
    syncRetailSubscriptionPlan?: boolean;
    registrationPaymentMethods?: 'ALL' | 'STRIPE' | 'CRYPTO';
    retailPermissionTemplate?: 'DEFAULT_RETAIL' | 'MARKET_SIGNALS_BASIC' | 'NONE';
    adminPermissionTemplate?: 'NONE' | 'OPERATIONS_MANAGER' | 'COMPLIANCE_OFFICER' | 'CUSTOMER_SUPPORT' | 'FINANCIAL_ANALYST' | 'RISK_MANAGER' | 'AUDITOR';
    retailPermissionAdds?: string[];
    retailPermissionRemoves?: string[];
    adminPermissionAdds?: string[];
    adminPermissionRemoves?: string[];
  }, token?: string): Promise<ApiResponse<any>> {
    return this.makeRequest<any>(`/admin/products/${productId}`, {
      method: 'PUT',
      body: JSON.stringify(product),
      headers: {
        'Content-Type': 'application/json'
      }
    }, token);
  }

  async toggleProductStatus(productId: number, token?: string): Promise<ApiResponse<any>> {
    return this.makeRequest<any>(`/admin/products/${productId}/toggle-status`, {
      method: 'POST'
    }, token);
  }

  async deleteProduct(productId: number, token?: string): Promise<ApiResponse<string>> {
    return this.makeRequest<string>(`/admin/products/${productId}`, {
      method: 'DELETE'
    }, token);
  }

  async getProductPermissionOptions(token?: string): Promise<ApiResponse<{
    retailTemplates: string[];
    adminTemplates: string[];
    retailPermissions: string[];
    adminPermissions: string[];
  }>> {
    return this.makeRequest('/admin/products/permission-options', {
      method: 'GET'
    }, token);
  }

  async getPermissionTemplates(token?: string): Promise<ApiResponse<any[]>> {
    return this.makeRequest('/admin/products/permission-templates', {
      method: 'GET'
    }, token);
  }

  async createPermissionTemplate(payload: {
    name: string;
    type: 'RETAIL' | 'ADMIN';
    description?: string;
    retailPermissions?: string[];
    adminPermissions?: string[];
  }, token?: string): Promise<ApiResponse<any>> {
    return this.makeRequest('/admin/products/permission-templates', {
      method: 'POST',
      body: JSON.stringify(payload),
      headers: {
        'Content-Type': 'application/json'
      }
    }, token);
  }

  async updatePermissionTemplate(id: number, payload: {
    description?: string;
    retailPermissions?: string[];
    adminPermissions?: string[];
  }, token?: string): Promise<ApiResponse<any>> {
    return this.makeRequest(`/admin/products/permission-templates/${id}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
      headers: {
        'Content-Type': 'application/json'
      }
    }, token);
  }

  async deletePermissionTemplate(id: number, token?: string): Promise<ApiResponse<string>> {
    return this.makeRequest(`/admin/products/permission-templates/${id}`, {
      method: 'DELETE'
    }, token);
  }

  // Trading Strategy Methods
  async getStrategyDetails(strategyId: number, token?: string): Promise<ApiResponse<any>> {
    return this.makeRequest<any>(`/api/admin/trading-strategies/${strategyId}`, {
      method: 'GET'
    }, token);
  }

  // Trading Strategy Analytics Methods
  async getStrategyTrades(
    strategyId: number,
    limit: number = 10,
    options?: { includeMeta?: boolean; token?: string }
  ): Promise<ApiResponse<any[] | { trades: any[]; meta: Record<string, unknown> }>> {
    const includeMeta = options?.includeMeta ? '&includeMeta=true' : '';
    return this.makeRequest<any>(
      `/api/admin/trading-strategies/${strategyId}/analytics/trades?limit=${limit}${includeMeta}`,
      { method: 'GET' },
      options?.token
    );
  }

  async getStrategyPositions(strategyId: number, token?: string): Promise<ApiResponse<any[]>> {
    return this.makeRequest<any[]>(`/api/admin/trading-strategies/${strategyId}/analytics/positions`, {
      method: 'GET'
    }, token);
  }

  // Hedge positions per strategy (per-leg LONG / SHORT)
  async getHedgePositionsForStrategy(strategyId: number, token?: string): Promise<ApiResponse<HedgePosition[]>> {
    return this.makeRequest<HedgePosition[]>(`/api/admin/trading-strategies/${strategyId}/hedge-positions`, {
      method: 'GET'
    }, token);
  }

  async getStrategyPnlHistory(strategyId: number, days: number = 30, token?: string): Promise<ApiResponse<any[]>> {
    return this.makeRequest<any[]>(`/api/admin/trading-strategies/${strategyId}/analytics/pnl-history?days=${days}`, {
      method: 'GET'
    }, token);
  }

  /**
   * KuCoin-sourced analytics (Option 2 — more accurate live data).
   * Returns: { summary, closedTrades, pnlHistory }
   * The DB-backed methods above (getStrategyTrades / getStrategyPositions / getStrategyPnlHistory)
   * remain intact and act as the rollback path when dataSource === 'db'.
   */
  async getStrategyKucoinAnalytics(strategyId: number, limit: number = 200, beforeMs?: string, token?: string): Promise<ApiResponse<{
    summary: {
      strategyId: number;
      strategyName: string;
      closedPositionCount: number;
      totalPnl: number;
      grossPnl?: number;
      totalTradeFee: number;
      totalFundingFee: number;
      netPnlAfterFees: number;
      winRate: number;
      winCount: number;
      lossCount: number;
      dataSource: string;
    };
    closedTrades: Array<{
      symbol: string;
      side: string;
      size: string;
      openPrice: string;
      closePrice: string;
      pnl: string;
      tradeFee: string;
      fundingFee: string;
      leverage: number;
      settleCurrency: string;
      openTime: string | null;
      closeTime: string | null;
      openTimeMs: number | null;
      closeTimeMs: number | null;
    }>;
    pnlHistory: Array<{
      date: string;
      pnl: number;
      cumulativePnl: number;
    }>;
    hasMore: boolean;
    nextPageId: string | null;
  }>> {
    const qs = beforeMs ? `&beforeMs=${encodeURIComponent(beforeMs)}` : '';
    return this.makeRequest(`/api/admin/trading-strategies/${strategyId}/kucoin-analytics?limit=${limit}${qs}`, {
      method: 'GET'
    }, token);
  }

  // iGaming Management Methods
  async getIGamingProviders(page: number = 0, size: number = 50, token?: string): Promise<ApiResponse<any>> {
    return this.makeRequest<any>(`/admin/igaming/providers?page=${page}&size=${size}`, {
      method: 'GET'
    }, token);
  }

  async toggleIGamingProvider(providerId: number, token?: string): Promise<ApiResponse<any>> {
    return this.makeRequest<any>(`/admin/igaming/providers/${providerId}/toggle`, {
      method: 'PUT'
    }, token);
  }

  async getIGamingGames(page: number = 0, size: number = 50, providerId?: number, category?: string, token?: string): Promise<ApiResponse<any>> {
    let url = `/admin/igaming/games?page=${page}&size=${size}`;
    if (providerId) url += `&providerId=${providerId}`;
    if (category) url += `&category=${category}`;
    return this.makeRequest<any>(url, {
      method: 'GET'
    }, token);
  }

  async toggleIGamingGame(gameId: number, token?: string): Promise<ApiResponse<any>> {
    return this.makeRequest<any>(`/admin/igaming/games/${gameId}/toggle`, {
      method: 'PUT'
    }, token);
  }

  async getIGamingSportsConfig(token?: string): Promise<ApiResponse<any>> {
    return this.makeRequest<any>(`/admin/igaming/sports/config`, {
      method: 'GET'
    }, token);
  }

  async getIGamingSettings(token?: string): Promise<ApiResponse<any>> {
    return this.makeRequest<any>(`/admin/igaming/settings`, {
      method: 'GET'
    }, token);
  }

  async updateIGamingSettings(settings: any, token?: string): Promise<ApiResponse<any>> {
    return this.makeRequest<any>(`/admin/igaming/settings`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(settings)
    }, token);
  }

  // Meme Coin Blacklist Management
  async getMemeBlacklist(token?: string): Promise<ApiResponse<MemeBlacklistEntry[]>> {
    return this.makeRequest<MemeBlacklistEntry[]>('/api/admin/meme-blacklist', {
      method: 'GET'
    }, token);
  }

  async addMemeBlacklistEntry(baseSymbol: string, reason?: string, token?: string): Promise<ApiResponse<MemeBlacklistEntry>> {
    return this.makeRequest<MemeBlacklistEntry>('/api/admin/meme-blacklist', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ baseSymbol, reason })
    }, token);
  }

  async removeMemeBlacklistEntry(baseSymbol: string, token?: string): Promise<ApiResponse<void>> {
    return this.makeRequest<void>(`/api/admin/meme-blacklist/${encodeURIComponent(baseSymbol)}`, {
      method: 'DELETE'
    }, token);
  }

  // IP Blocklist Management
  async getIpBlocklist(token?: string): Promise<ApiResponse<any[]>> {
    return this.makeRequest<any[]>('/api/admin/ip-blocklist', {
      method: 'GET'
    }, token);
  }

  async addIpToBlocklist(
    ipAddress: string,
    reason?: string,
    token?: string,
    entryType: 'IP' | 'EMAIL' | 'PHONE' = 'IP'
  ): Promise<ApiResponse<any>> {
    return this.makeRequest<any>('/api/admin/ip-blocklist', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        entryType,
        value: ipAddress,
        ipAddress: entryType === 'IP' ? ipAddress : undefined,
        reason
      })
    }, token);
  }

  async setIpBlocklistEnabled(id: number, enabled: boolean, token?: string): Promise<ApiResponse<any>> {
    return this.makeRequest<any>(`/api/admin/ip-blocklist/${id}/enabled`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ enabled })
    }, token);
  }

  async deleteIpFromBlocklist(id: number, token?: string): Promise<ApiResponse<void>> {
    return this.makeRequest<void>(`/api/admin/ip-blocklist/${id}`, {
      method: 'DELETE'
    }, token);
  }

  // ─── Platform Coins & Airdrops (Phase 1) ───────────────────────────────────
  async getPlatformCoins(token?: string): Promise<ApiResponse<any[]>> {
    return this.makeRequest<any[]>('/api/admin/platform-coins', { method: 'GET' }, token);
  }

  async createPlatformCoin(coinData: any, token?: string): Promise<ApiResponse<any>> {
    return this.makeRequest<any>('/api/admin/platform-coins', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(coinData),
    }, token);
  }

  async updatePlatformCoin(id: number, coinData: any, token?: string): Promise<ApiResponse<any>> {
    return this.makeRequest<any>(`/api/admin/platform-coins/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(coinData),
    }, token);
  }

  async getAirdropCampaigns(platformCoinId?: number, token?: string): Promise<ApiResponse<any[]>> {
    const qs = platformCoinId != null ? `?platformCoinId=${platformCoinId}` : '';
    return this.makeRequest<any[]>(`/api/admin/airdrop-campaigns${qs}`, { method: 'GET' }, token);
  }

  async getAirdropCampaign(id: number, includeRecipients: boolean = true, token?: string): Promise<ApiResponse<any>> {
    return this.makeRequest<any>(
      `/api/admin/airdrop-campaigns/${id}?includeRecipients=${includeRecipients}`,
      { method: 'GET' },
      token
    );
  }

  async createAirdropCampaign(campaignData: any, token?: string): Promise<ApiResponse<any>> {
    return this.makeRequest<any>('/api/admin/airdrop-campaigns', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(campaignData),
    }, token);
  }

  async previewAirdropCampaign(id: number, token?: string): Promise<ApiResponse<any>> {
    return this.makeRequest<any>(`/api/admin/airdrop-campaigns/${id}/preview`, {
      method: 'POST',
    }, token);
  }

  async dryRunAirdropCampaign(id: number, token?: string): Promise<ApiResponse<any>> {
    return this.makeRequest<any>(`/api/admin/airdrop-campaigns/${id}/dry-run`, {
      method: 'POST',
    }, token);
  }

  async cancelAirdropCampaign(id: number, token?: string): Promise<ApiResponse<any>> {
    return this.makeRequest<any>(`/api/admin/airdrop-campaigns/${id}/cancel`, {
      method: 'POST',
    }, token);
  }

  async getAirdropExecuteConfig(token?: string): Promise<ApiResponse<any>> {
    return this.makeRequest<any>('/api/admin/airdrop-campaigns/execute-config', { method: 'GET' }, token);
  }

  async executeAirdropCampaign(
    id: number,
    body: { fundingWalletId: number; confirmLiveSend: boolean },
    token?: string
  ): Promise<ApiResponse<any>> {
    return this.makeRequest<any>(`/api/admin/airdrop-campaigns/${id}/execute`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }, token);
  }

  async listAdminCryptoWallets(token?: string): Promise<ApiResponse<any[]>> {
    return this.makeRequest<any[]>('/admin/crypto/wallets/list', { method: 'GET' }, token);
  }

  async getPlatformCoinDeployConfig(token?: string): Promise<ApiResponse<any>> {
    return this.makeRequest<any>('/api/admin/platform-coins/deploy-config', { method: 'GET' }, token);
  }

  async deployPlatformCoinTestnet(
    id: number,
    body: { fundingWalletId: number; confirmDeploy: boolean },
    token?: string
  ): Promise<ApiResponse<any>> {
    return this.makeRequest<any>(`/api/admin/platform-coins/${id}/deploy-testnet`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }, token);
  }

  // ─── Stablecoin Config Management ──────────────────────────────────────────
  async getStablecoinConfigs(token?: string): Promise<ApiResponse<StablecoinConfigItem[]>> {
    return this.makeRequest<StablecoinConfigItem[]>('/api/admin/stablecoin-configs', { method: 'GET' }, token);
  }

  async createStablecoinConfig(dto: Partial<StablecoinConfigItem>, token?: string): Promise<ApiResponse<StablecoinConfigItem>> {
    return this.makeRequest<StablecoinConfigItem>('/api/admin/stablecoin-configs', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(dto)
    }, token);
  }

  async updateStablecoinConfig(id: number, dto: Partial<StablecoinConfigItem>, token?: string): Promise<ApiResponse<StablecoinConfigItem>> {
    return this.makeRequest<StablecoinConfigItem>(`/api/admin/stablecoin-configs/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(dto)
    }, token);
  }

  async setStablecoinConfigStatus(id: number, enabled: boolean, token?: string): Promise<ApiResponse<StablecoinConfigItem>> {
    return this.makeRequest<StablecoinConfigItem>(`/api/admin/stablecoin-configs/${id}/status`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ enabled })
    }, token);
  }

  async deleteStablecoinConfig(id: number, token?: string): Promise<ApiResponse<void>> {
    return this.makeRequest<void>(`/api/admin/stablecoin-configs/${id}`, { method: 'DELETE' }, token);
  }

  // ═══════════════════════════════════════════════════════════════════════
  // SUPER-ADMIN: Subscription Management
  // ═══════════════════════════════════════════════════════════════════════

  /** Retail Plans */
  async getRetailPlans(token?: string): Promise<ApiResponse<any[]>> {
    return this.makeRequest<any[]>('/api/superadmin/subscriptions/retail/plans', {}, token);
  }

  async updateRetailPlanPrice(planId: number, price: number, token?: string): Promise<ApiResponse<any>> {
    return this.makeRequest<any>(`/api/superadmin/subscriptions/retail/plans/${planId}/price`, {
      method: 'PATCH',
      body: JSON.stringify({ price }),
    }, token);
  }

  /** Retail plan: which methods appear for renew / upgrade (ALL | STRIPE | CRYPTO). */
  async updateRetailPlanRegistrationPaymentMethods(
    planId: number,
    registrationPaymentMethods: 'ALL' | 'STRIPE' | 'CRYPTO',
    token?: string
  ): Promise<ApiResponse<any>> {
    return this.makeRequest<any>(
      `/api/superadmin/subscriptions/retail/plans/${planId}/registration-payment-methods`,
      {
        method: 'PATCH',
        body: JSON.stringify({ registrationPaymentMethods }),
      },
      token
    );
  }

  /** Retail User Subscriptions */
  async getSuperAdminRetailUsers(token?: string): Promise<ApiResponse<any[]>> {
    return this.makeRequest<any[]>('/api/superadmin/subscriptions/retail/users', {}, token);
  }

  async getUserSubscriptionHistory(userId: number, token?: string): Promise<ApiResponse<any[]>> {
    return this.makeRequest<any[]>(`/api/superadmin/subscriptions/retail/users/${userId}`, {}, token);
  }

  async grantRetailSubscription(userId: number, planId: number, months: number, token?: string): Promise<ApiResponse<any>> {
    return this.makeRequest<any>(`/api/superadmin/subscriptions/retail/users/${userId}/grant`, {
      method: 'POST',
      body: JSON.stringify({ planId, months }),
    }, token);
  }

  async renewRetailSubscription(subscriptionId: number, months: number, token?: string): Promise<ApiResponse<any>> {
    return this.makeRequest<any>(`/api/superadmin/subscriptions/retail/${subscriptionId}/renew`, {
      method: 'POST',
      body: JSON.stringify({ months }),
    }, token);
  }

  async suspendRetailSubscription(userId: number, token?: string): Promise<ApiResponse<any>> {
    return this.makeRequest<any>(`/api/superadmin/subscriptions/retail/users/${userId}/suspend`, {
      method: 'POST',
    }, token);
  }

  /** R-B01: Cancel a retail user's subscription */
  async cancelRetailSubscription(userId: number, token?: string): Promise<ApiResponse<any>> {
    return this.makeRequest<any>(`/api/superadmin/subscriptions/retail/users/${userId}/cancel`, {
      method: 'POST',
    }, token);
  }

  /** R-B03: Change a retail user's plan tier */
  async changeRetailUserPlan(userId: number, planId: number, token?: string): Promise<ApiResponse<any>> {
    return this.makeRequest<any>(`/api/superadmin/subscriptions/retail/users/${userId}/plan`, {
      method: 'PATCH',
      body: JSON.stringify({ planId }),
    }, token);
  }

  /** Admin Plans */
  async getAdminPlans(token?: string): Promise<ApiResponse<any[]>> {
    return this.makeRequest<any[]>('/api/superadmin/subscriptions/admin/plans', {}, token);
  }

  async updateAdminPlanPrice(planId: number, price: number, token?: string): Promise<ApiResponse<any>> {
    return this.makeRequest<any>(`/api/superadmin/subscriptions/admin/plans/${planId}/price`, {
      method: 'PATCH',
      body: JSON.stringify({ price }),
    }, token);
  }

  /** Admin plan: base methods for operator self-service renewal (ALL | STRIPE | CRYPTO); assigned products can narrow further. */
  async updateAdminPlanRegistrationPaymentMethods(
    planId: number,
    registrationPaymentMethods: 'ALL' | 'STRIPE' | 'CRYPTO',
    token?: string
  ): Promise<ApiResponse<any>> {
    return this.makeRequest<any>(
      `/api/superadmin/subscriptions/admin/plans/${planId}/registration-payment-methods`,
      {
        method: 'PATCH',
        body: JSON.stringify({ registrationPaymentMethods }),
      },
      token
    );
  }

  /** Admin Operator Subscriptions */
  async getSuperAdminOperators(token?: string): Promise<ApiResponse<any[]>> {
    return this.makeRequest<any[]>('/api/superadmin/subscriptions/admin/operators', {}, token);
  }

  async getAdminSubscriptionHistory(adminUserId: number, token?: string): Promise<ApiResponse<any[]>> {
    return this.makeRequest<any[]>(`/api/superadmin/subscriptions/admin/operators/${adminUserId}`, {}, token);
  }

  async grantAdminSubscription(adminUserId: number, planId: number, months: number, token?: string): Promise<ApiResponse<any>> {
    return this.makeRequest<any>(`/api/superadmin/subscriptions/admin/operators/${adminUserId}/grant`, {
      method: 'POST',
      body: JSON.stringify({ planId, months }),
    }, token);
  }

  async renewAdminSubscription(subscriptionId: number, months: number, token?: string): Promise<ApiResponse<any>> {
    return this.makeRequest<any>(`/api/superadmin/subscriptions/admin/${subscriptionId}/renew`, {
      method: 'POST',
      body: JSON.stringify({ months }),
    }, token);
  }

  async suspendAdminSubscription(adminUserId: number, token?: string): Promise<ApiResponse<any>> {
    return this.makeRequest<any>(`/api/superadmin/subscriptions/admin/operators/${adminUserId}/suspend`, {
      method: 'POST',
    }, token);
  }

  /** R-B02: Cancel an admin operator's subscription */
  async cancelAdminSubscription(adminUserId: number, token?: string): Promise<ApiResponse<any>> {
    return this.makeRequest<any>(`/api/superadmin/subscriptions/admin/operators/${adminUserId}/cancel`, {
      method: 'POST',
    }, token);
  }

  /** R-B04: Change an admin operator's plan tier */
  async changeAdminOperatorPlan(adminUserId: number, planId: number, token?: string): Promise<ApiResponse<any>> {
    return this.makeRequest<any>(`/api/superadmin/subscriptions/admin/operators/${adminUserId}/plan`, {
      method: 'PATCH',
      body: JSON.stringify({ planId }),
    }, token);
  }

  /** All recorded payments for a retail user (across all their subscription records) */
  async getRetailUserPayments(userId: number, token?: string): Promise<ApiResponse<any[]>> {
    return this.makeRequest<any[]>(`/api/superadmin/subscriptions/retail/users/${userId}/payments`, {}, token);
  }

  /** All recorded payments for an admin operator (across all their subscription records) */
  async getAdminOperatorPayments(adminUserId: number, token?: string): Promise<ApiResponse<any[]>> {
    return this.makeRequest<any[]>(`/api/superadmin/subscriptions/admin/operators/${adminUserId}/payments`, {}, token);
  }

  /** Revenue Summary */
  async getSubscriptionRevenueSummary(token?: string): Promise<ApiResponse<any>> {
    return this.makeRequest<any>('/api/superadmin/subscriptions/revenue/summary', {}, token);
  }

  /** Retail subscription upgrade requests awaiting crypto payment verification */
  async getPendingSubscriptionCryptoVerifications(token?: string): Promise<ApiResponse<any[]>> {
    return this.makeRequest<any[]>('/api/superadmin/subscriptions/retail/crypto/pending', {}, token);
  }

  /**
   * Approve or reject a retail subscription crypto payment proof.
   * Body matches backend: upgradeRequestId, approved (required), optional note.
   */
  async verifyRetailSubscriptionCryptoPayment(
    body: { upgradeRequestId: number; approved: boolean; note?: string },
    token?: string
  ): Promise<ApiResponse<any>> {
    return this.makeRequest<any>('/api/superadmin/subscriptions/retail/crypto/verify', {
      method: 'POST',
      body: JSON.stringify(body),
    }, token);
  }

  /**
   * Cancel unfinished retail subscription registration/upgrade payment flow.
   * Body: upgradeRequestId (required), optional note.
   */
  async cancelRetailSubscriptionCryptoOrPaymentFlow(
    body: { upgradeRequestId: number; note?: string },
    token?: string
  ): Promise<ApiResponse<any>> {
    return this.makeRequest<any>('/api/superadmin/subscriptions/retail/crypto/cancel', {
      method: 'POST',
      body: JSON.stringify(body),
    }, token);
  }

  /** Self-service: admin operator checks their own subscription status */
  async getAdminSubscriptionStatus(token?: string): Promise<ApiResponse<any>> {
    return this.makeRequest<any>('/api/admin/subscription/status', {}, token);
  }

  /** Active admin subscription plans (renewal / pricing UI) */
  async getAdminSubscriptionPlans(token?: string): Promise<ApiResponse<any>> {
    return this.makeRequest<any>('/api/admin/subscription/plans', {}, token);
  }

  /** Start paid admin subscription renewal (compliance, Stripe or crypto). */
  async startAdminSubscriptionRenewal(
    planId: number,
    paymentMethod: 'STRIPE' | 'CRYPTO' = 'STRIPE',
    cryptoBlockchain?: string,
    cryptoSymbol?: string,
    token?: string
  ): Promise<ApiResponse<any>> {
    const body: any = { planId, paymentMethod };
    if (paymentMethod === 'CRYPTO') {
      body.cryptoBlockchain = cryptoBlockchain;
      body.cryptoSymbol = cryptoSymbol;
    }
    return this.makeRequest<any>('/api/admin/subscription/renew/start', {
      method: 'POST',
      body: JSON.stringify(body),
    }, token);
  }

  async getAdminSubscriptionRenewPending(token?: string): Promise<ApiResponse<any>> {
    return this.makeRequest<any>('/api/admin/subscription/renew/pending', {}, token);
  }

  async confirmAdminSubscriptionRenewalPayment(
    body: { sessionId?: string } | undefined,
    token?: string
  ): Promise<ApiResponse<any>> {
    return this.makeRequest<any>('/api/admin/subscription/renew/confirm-payment', {
      method: 'POST',
      body: JSON.stringify(body ?? {}),
    }, token);
  }

  async submitAdminSubscriptionCryptoPayment(
    body: { renewalRequestId: number; txHash: string },
    token?: string
  ): Promise<ApiResponse<any>> {
    return this.makeRequest<any>('/api/admin/subscription/renew/submit-crypto-payment', {
      method: 'POST',
      body: JSON.stringify(body),
    }, token);
  }

  async payAdminSubscriptionWithWallet(
    body: { renewalRequestId: number },
    token?: string
  ): Promise<ApiResponse<any>> {
    return this.makeRequest<any>('/api/admin/subscription/renew/pay-with-wallet', {
      method: 'POST',
      body: JSON.stringify(body),
    }, token);
  }

  async getPendingAdminSubscriptionCryptoVerifications(token?: string): Promise<ApiResponse<any[]>> {
    return this.makeRequest<any[]>('/api/superadmin/subscriptions/admin/crypto/pending', {}, token);
  }

  async verifyAdminSubscriptionCryptoPayment(
    body: { renewalRequestId: number; approved: boolean; note?: string },
    token?: string
  ): Promise<ApiResponse<any>> {
    return this.makeRequest<any>('/api/superadmin/subscriptions/admin/crypto/verify', {
      method: 'POST',
      body: JSON.stringify(body),
    }, token);
  }

  /**
   * Cancel unfinished admin subscription renewal payment flow.
   * Body: renewalRequestId (required), optional note.
   */
  async cancelAdminSubscriptionCryptoOrPaymentFlow(
    body: { renewalRequestId: number; note?: string },
    token?: string
  ): Promise<ApiResponse<any>> {
    return this.makeRequest<any>('/api/superadmin/subscriptions/admin/crypto/cancel', {
      method: 'POST',
      body: JSON.stringify(body),
    }, token);
  }

  /** Admin Market Intelligence board (commodities / FX / crypto / stocks + news triggers) */
  async getMarketsBoard(token?: string): Promise<ApiResponse<any>> {
    return this.makeRequest<any>('/api/admin/markets/board', {}, token);
  }

  async getMarketsHistory(instrumentId: string, range: string = '1mo', token?: string): Promise<ApiResponse<any>> {
    return this.makeRequest<any>(
      `/api/admin/markets/history/${encodeURIComponent(instrumentId)}?range=${encodeURIComponent(range)}`,
      {},
      token
    );
  }
}

export const adminApiService = new AdminApiService();

// Meme Blacklist Types
export interface MemeBlacklistEntry {
  id: number;
  baseSymbol: string;
  reason?: string;
  createdAt: string;
  updatedAt: string;
  createdBy?: string;
}

export interface StrategyRegimeReviewTrade {
  id?: number;
  symbol?: string;
  exitReason?: string;
  exitCategory?: string;
  pnl?: number | string;
  fees?: number | string;
  closedAt?: string;
  regime?: string;
  regimeLabel?: string;
  regimeSource?: string;
}

export interface StrategyRegimeReviewTuneEvent {
  id?: number;
  appliedAt?: string;
  marketRegime?: string;
  marketRegimeLabel?: string;
  outcome?: string;
  triggerType?: string;
  changeCount?: number;
  netPnlAtApply?: number | string;
  netPnlAfter?: number | string;
  /** US-RPR-03/RLB-03: gate state changes, e.g. ["longBlockOn","scalperOff"]. */
  gateChanges?: string[];
}

export interface StrategyRegimeReviewDailySummary {
  date: string;
  closeCount: number;
  netPnl: number | string;
  winRate: number | string;
  dominantRegime?: string;
  dominantRegimeLabel?: string;
  tuneEventCount: number;
  tuneEvents: StrategyRegimeReviewTuneEvent[];
  configSnapshotAfter?: Record<string, unknown> | null;
  trades: StrategyRegimeReviewTrade[];
}

export interface StrategyRegimeReviewMatrixRow {
  regime: string;
  regimeLabel?: string;
  tradeCount: number;
  storedCount: number;
  inferredCount: number;
  netPnl: number | string;
  totalFees?: number | string;
  winRate: number | string;
  avgPnl: number | string;
  bestCoin?: string | null;
  worstCoin?: string | null;
  /** US-RPR-01: days this was the dominant regime in the selected range. */
  dominantDayCount?: number;
}

/** US-RPR-02: one entry per (date, regime) for the stacked PnL chart. */
export interface PnlByRegimeDay {
  date: string;
  regime: string;
  regimeLabel?: string;
  netPnl: number | string;
  closeCount: number;
}

export interface StrategyRegimeReviewExitReasonRow {
  regime: string;
  regimeLabel?: string;
  microTsl: number;
  classicTsl: number;
  classicSl: number;
  takeProfit: number;
  /** US-SCL-03: closes that fired via scalper-mode take-profit. */
  scalperTp?: number;
  other: number;
}

export interface StrategyRegimeReviewData {
  strategyId: number;
  strategyName?: string;
  fromDate: string;
  toDate: string;
  regimeLabels?: Record<string, string>;
  totalCloses: number;
  totalNetPnl: number | string;
  storedRegimeCount: number;
  inferredRegimeCount: number;
  unknownRegimeCount: number;
  dailySummaries: StrategyRegimeReviewDailySummary[];
  regimeMatrix: StrategyRegimeReviewMatrixRow[];
  exitReasonMatrix: StrategyRegimeReviewExitReasonRow[];
  /** US-RPR-02: per-day PnL grouped by dominant regime for the stacked bar chart. */
  pnlByRegimeByDay?: PnlByRegimeDay[];
}

// US-RDX-01: regime status panel interfaces
export interface RegimeStatusStrategyRow {
  id: number;
  name: string;
  lastRegime: string;
  entryBlockEnabled: boolean;
  entryBlocked: boolean;
  longBlockEnabled: boolean;
  longBlocked: boolean;
  scalperEnabled: boolean;
  scalperActive: boolean;
  aiAutoTuneEnabled: boolean;
}

export interface RegimeStatusResult {
  currentRegime: string;
  regimeLabel: string;
  generatedAt: number;
  strategies: RegimeStatusStrategyRow[];
}

export interface AiStrategySuggestResult {
  marketRegime: string;
  regimeLabel: string;
  confidence: number;
  volatilityPreset: 'moderate' | 'conservative' | 'aggressive' | string;
  reasoning: string[];
  marketSnapshot?: Record<string, unknown>;
  suggestedConfig: Record<string, unknown>;
}

export interface CoinPrediction {
  coinName: string;
  symbol: string;
  score: number;
  tier: string;          // "🔥 HOT" | "✅ GOOD" | "🟡 NEUTRAL" | "⚠️ CAUTION"
  tierColor: string;
  hasHistory: boolean;
  tradeCount: number;
  winRate: number;       // 0-100
  totalPnl: number;
  volumeRank: number;
  volume24h: number;
  change24h: number;
  volatility: number;
  price: number;
  pnlScore: number;
  winRateScore: number;
  volumeScore: number;
  volatilityScore: number;
  predictedRank: number;
}

export type { 
  ApiResponse, 
  AdminCustomer, 
  AdminAccount, 
  AdminTransaction, 
  TransactionFilter
};

// Stablecoin Config Types
export interface StablecoinConfigItem {
  id: number;
  symbol: string;
  name: string;
  network: string;
  enabled: boolean;
  transactionLimit: number | null;
  dailyLimit: number | null;
  minimumAmount: number;
  description: string | null;
  contractAddress: string | null;
  icon: string | null;
  convertEnabled: boolean;
  sendEnabled: boolean;
  receiveEnabled: boolean;
  createdAt: string;
  updatedAt: string;
}

// KYT (Know Your Transaction) API methods
export const kytApiService = {
  /**
   * Recalculate Bitcoin transaction amounts
   */
  recalculateBitcoinAmounts: async (): Promise<ApiResponse<any>> => {
    try {
      const response = await axiosInstance.post('/api/admin/kyt/recalculate-bitcoin-amounts');
      return {
        success: true,
        data: response.data
      };
    } catch (error: any) {
      console.error('Error recalculating Bitcoin amounts:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to recalculate Bitcoin amounts'
      };
    }
  },

  /**
   * Fetch Stellar transaction data for a specific address
   */
  fetchStellarData: async (address: string): Promise<ApiResponse<any>> => {
    try {
      const response = await axiosInstance.post('/api/admin/kyt/fetch-stellar-data', { address });
      return {
        success: true,
        data: response.data
      };
    } catch (error: any) {
      console.error('Error fetching Stellar data:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to fetch Stellar data'
      };
    }
  }
  ,
  /**
   * Fetch Ripple transaction data for a specific address
   */
  fetchRippleData: async (address: string): Promise<ApiResponse<any>> => {
    try {
      const response = await axiosInstance.post('/api/admin/kyt/fetch-ripple-data', { address });
      return {
        success: true,
        data: response.data
      };
    } catch (error: any) {
      console.error('Error fetching Ripple data:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to fetch Ripple data'
      };
    }
  }
}; 